import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteConfigProvider, useSiteConfig } from './context/SiteConfigContext';
import Login from './components/Login';
import CalendarView from './components/Calendar';
// SetupAdmin sayfası güvenlik nedeniyle kaldırıldı
import { 
  subscribeToDebtors, 
  subscribeToSettings,
  subscribeToUsers,
  addDebtor, 
  updateDebtor,
  addSetting,
  updateSetting,
  deleteSetting,
  deleteUser,
  updateUser,
  initDefaultSettings
} from './services/firestoreService';
import { HexColorPicker } from 'react-colorful';
import { 
  Users, 
  FileText, 
  DollarSign, 
  Gavel, 
  Search, 
  Plus, 
  Calendar, 
  AlertTriangle, 
  CheckCircle, 
  Settings, 
  History,
  X,
  Save,
  Trash2,
  MoreVertical,
  BarChart3,
  UserPlus,
  Edit2,
  PieChart,
  CreditCard,
  Phone,
  ChevronDown,
  Bell,
  CalendarCheck,
  User,
  Clock,
  FolderOpen,
  ArrowLeft,
  Timer,
  Wallet,
  ArrowUpRight,
  LogOut,
  Menu,
  Upload,
  Image,
  Palette,
  Type,
  Globe,
  Sun,
  Moon,
  Home,
  Car,
  Package
} from 'lucide-react';
import './App.css';

// --- HELPER FUNCTIONS ---

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return '0,00 ₺';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('tr-TR');
};

const calculateFinalized = (notificationDate, mukhtarDate) => {
  const targetDateStr = mukhtarDate || notificationDate;
  if (!targetDateStr) return { isFinalized: false, daysLeft: null };

  const targetDate = new Date(targetDateStr);
  const today = new Date();
  
  const finalizeDate = new Date(targetDate);
  finalizeDate.setDate(targetDate.getDate() + 7);

  const todayTime = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const finalizeTime = new Date(finalizeDate.getFullYear(), finalizeDate.getMonth(), finalizeDate.getDate()).getTime();

  const isFinalized = todayTime > finalizeTime;
  const daysLeft = Math.ceil((finalizeTime - todayTime) / (1000 * 60 * 60 * 24));

  return { isFinalized, daysLeft };
};

const calculatePromiseCountdown = (targetDateStr) => {
  if (!targetDateStr) return { daysLeft: null, isOverdue: false };
  
  const targetDate = new Date(targetDateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  const diffTime = targetDate - today;
  const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return { daysLeft, isOverdue: daysLeft < 0 };
};

// --- CUSTOM COMPONENTS ---

function SearchableSelect({ options, value, onChange, placeholder }) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const filteredOptions = options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedOption = options.find(o => o.value === value);

    return (
        <div className="relative" ref={wrapperRef}>
            <div 
                className="w-full border border-slate-300 rounded-lg p-2.5 bg-white flex justify-between items-center cursor-pointer focus:ring-2 focus:ring-amber-500"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className={`block truncate ${!selectedOption ? 'text-slate-400' : 'text-slate-800'}`}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown size={16} className="text-slate-400" />
            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    <div className="p-2 sticky top-0 bg-white border-b border-slate-100">
                        <div className="relative">
                            <Search size={14} className="absolute left-2 top-2.5 text-slate-400"/>
                            <input 
                                type="text"
                                className="w-full border border-slate-200 rounded p-1.5 pl-8 text-sm focus:outline-none focus:border-amber-500"
                                placeholder="Ara..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                        </div>
                    </div>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(option => (
                            <div 
                                key={option.value}
                                className={`px-4 py-2 text-sm hover:bg-slate-50 cursor-pointer ${value === option.value ? 'bg-amber-50 text-amber-900' : 'text-slate-700'}`}
                                onClick={() => {
                                    onChange(option.value);
                                    setIsOpen(false);
                                    setSearchTerm('');
                                }}
                            >
                                {option.label}
                            </div>
                        ))
                    ) : (
                        <div className="p-3 text-sm text-slate-400 text-center">Sonuç bulunamadı</div>
                    )}
                </div>
            )}
        </div>
    );
}

function StatCard({ icon, title, value, color }) {
  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4 transition-transform hover:-translate-y-1">
      <div className={`${color} p-4 rounded-lg text-white shadow-lg shadow-blue-100`}>
        {React.cloneElement(icon, { size: 24 })}
      </div>
      <div>
        <div className="text-sm text-slate-500 font-medium">{title}</div>
        <div className="text-2xl font-bold text-slate-800">{value}</div>
      </div>
    </div>
  );
}

function SidebarItem({ icon, label, active, onClick, primaryColor }) {
  const color = primaryColor || '#f59e0b';
  return (
    <button 
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 
      ${active 
        ? 'text-white shadow-lg' 
        : 'text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white hover:shadow-md border border-transparent hover:border-slate-200 dark:hover:border-slate-600'}`}
      style={active ? { 
        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
        boxShadow: `0 4px 15px ${color}40`
      } : {}}
    >
      {React.cloneElement(icon, { size: 20 })}
      <span className="font-medium text-sm whitespace-nowrap">{label}</span>
    </button>
  );
}

// --- MAIN APP CONTENT ---

function MainApp() {
  const { userData, logout } = useAuth();
  const { config, loading: configLoading } = useSiteConfig();
  const [view, setView] = useState('dashboard');
  const [debtors, setDebtors] = useState([]);
  const [settings, setSettings] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDebtor, setSelectedDebtor] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddDebtorModalOpen, setIsAddDebtorModalOpen] = useState(false);
  const [editingDebtor, setEditingDebtor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deletedLogs, setDeletedLogs] = useState([]);
  const [darkMode, setDarkMode] = useState(() => {
    // localStorage'dan dark mode tercihini al - client side'da çalışacak şekilde
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });

  // Dark mode değiştiğinde body class'ını güncelle
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Silinen logları localStorage'dan yükle
  useEffect(() => {
    const savedLogs = localStorage.getItem('deletedLogs');
    if (savedLogs) {
      setDeletedLogs(JSON.parse(savedLogs));
    }
  }, []);

  // Silinen log ekleme fonksiyonu
  const addDeletedLog = (type, description, details) => {
    const newLog = {
      id: Date.now(),
      type,
      description,
      details,
      deletedBy: CURRENT_USER.fullName,
      deletedAt: new Date().toISOString()
    };
    const updatedLogs = [newLog, ...deletedLogs];
    setDeletedLogs(updatedLogs);
    localStorage.setItem('deletedLogs', JSON.stringify(updatedLogs));
  };

  const CURRENT_USER = userData || { fullName: 'Kullanıcı', role: 'STAFF' };

  // Subscribe to Firestore data
  useEffect(() => {
    const initData = async () => {
      await initDefaultSettings();
    };
    initData();

    const unsubDebtors = subscribeToDebtors(setDebtors);
    const unsubSettings = subscribeToSettings(setSettings);
    const unsubUsers = subscribeToUsers(setUsers);
    
    setLoading(false);

    return () => {
      unsubDebtors();
      unsubSettings();
      unsubUsers();
    };
  }, []);

  // Modal açıkken debtors güncellenirse selectedDebtor'ı da güncelle
  useEffect(() => {
    if (selectedDebtor && isModalOpen) {
      const updatedDebtor = debtors.find(d => d.id === selectedDebtor.id);
      if (updatedDebtor) {
        setSelectedDebtor(updatedDebtor);
      }
    }
  }, [debtors, isModalOpen]);

  // Dashboard Stats
  const stats = useMemo(() => {
    const totalFiles = debtors.length;
    const totalDebt = debtors.reduce((acc, curr) => acc + (curr.totalDebt || 0), 0);
    const totalPaid = debtors.reduce((acc, curr) => acc + (curr.totalPaid || 0), 0);
    const activeFiles = debtors.filter(d => !d.isClosed).length;
    return { totalFiles, totalDebt, totalPaid, activeFiles };
  }, [debtors]);

  // Actions
  const handleSaveDebtor = async (formData) => {
    if (editingDebtor) {
      const newTotalDebt = parseFloat(formData.totalDebt);
      const newCostAmount = parseFloat(formData.costAmount || 0);
      const currentDebtor = debtors.find(d => d.id === editingDebtor.id);
      const newBalance = (newTotalDebt + newCostAmount) - (currentDebtor?.totalPaid || 0);
      
      await updateDebtor(editingDebtor.id, {
        ...formData,
        totalDebt: newTotalDebt,
        costAmount: newCostAmount,
        balance: newBalance
      });
      setEditingDebtor(null);
    } else {
      const totalDebt = parseFloat(formData.totalDebt);
      const costAmount = parseFloat(formData.costAmount || 0);
      
      const newDebtor = {
        ...formData,
        totalDebt: totalDebt,
        costAmount: costAmount,
        totalPaid: 0,
        remittedAmount: 0,
        balance: totalDebt + costAmount,
        isClosed: false,
        notificationDate: null,
        mukhtarDate: null,
        paymentPromiseDate: formData.paymentPromiseDate || null,
        notes: [{ id: Date.now(), type: 'SYSTEM_LOG', content: 'Dosya sisteme eklendi.', date: new Date().toISOString(), author: CURRENT_USER.fullName }],
        payments: [],
        remittances: []
      };
      await addDebtor(newDebtor);
    }
    setIsAddDebtorModalOpen(false);
  };

  const handleEditClick = (debtor) => {
    setEditingDebtor(debtor);
    setIsAddDebtorModalOpen(true);
  };

  const handleAddPayment = async (debtorId, amount, type, note = '') => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) return;

    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    const newPaid = (debtor.totalPaid || 0) + numAmount;
    const newBalance = ((debtor.totalDebt || 0) + (debtor.costAmount || 0)) - newPaid;
    const newPayment = { id: Date.now(), amount: numAmount, type, date: new Date().toISOString(), note, author: CURRENT_USER.fullName };
    
    const paymentLog = { 
      id: Date.now() + 1, 
      type: 'SYSTEM_LOG', 
      content: `Sistem: ${formatCurrency(numAmount)} (${type}) tahsilat yapıldı. ${note ? `Not: ${note}` : ''}`, 
      date: new Date().toISOString(),
      author: CURRENT_USER.fullName
    };
    
    await updateDebtor(debtorId, {
      totalPaid: newPaid,
      balance: newBalance,
      payments: [...(debtor.payments || []), newPayment],
      notes: [paymentLog, ...(debtor.notes || [])]
    });
  };

  const handleAddRemittance = async (debtorId, amount, note = '') => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) return;

    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    const newRemitted = (debtor.remittedAmount || 0) + numAmount;
    
    const newRemittance = {
      id: Date.now(),
      amount: numAmount,
      date: new Date().toISOString(),
      note,
      author: CURRENT_USER.fullName
    };

    const remittanceLog = {
      id: Date.now() + 1,
      type: 'SYSTEM_LOG',
      content: `Sistem: Alacaklıya ${formatCurrency(numAmount)} ödeme (reddiyat) yapıldı.`,
      date: new Date().toISOString(),
      author: CURRENT_USER.fullName
    };

    await updateDebtor(debtorId, {
      remittedAmount: newRemitted,
      remittances: [...(debtor.remittances || []), newRemittance],
      notes: [remittanceLog, ...(debtor.notes || [])]
    });
  };

  const handleAddInteraction = async (debtorId, noteContent) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    const newNote = {
      id: Date.now(),
      type: 'INTERACTION', 
      content: noteContent,
      date: new Date().toISOString(),
      author: CURRENT_USER.fullName
    };

    await updateDebtor(debtorId, {
      notes: [newNote, ...(debtor.notes || [])]
    });
  };

  // Mal varlığı ekleme
  const handleAddAsset = async (debtorId, asset) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    const newAsset = {
      id: Date.now(),
      type: asset.type,
      description: asset.description,
      addedAt: new Date().toISOString(),
      addedBy: CURRENT_USER.fullName
    };

    const newLog = { 
      id: Date.now() + 1, 
      type: 'SYSTEM_LOG', 
      content: `Sistem: Mal varlığı eklendi - ${asset.type}: ${asset.description}`, 
      date: new Date().toISOString(),
      author: CURRENT_USER.fullName
    };

    await updateDebtor(debtorId, {
      assets: [newAsset, ...(debtor.assets || [])],
      notes: [newLog, ...(debtor.notes || [])]
    });
  };

  // Mal varlığı silme
  const handleRemoveAsset = async (debtorId, assetId) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    const assetToRemove = (debtor.assets || []).find(a => a.id === assetId);
    if (!assetToRemove) return;

    const newLog = { 
      id: Date.now(), 
      type: 'SYSTEM_LOG', 
      content: `Sistem: Mal varlığı silindi - ${assetToRemove.type}: ${assetToRemove.description}`, 
      date: new Date().toISOString(),
      author: CURRENT_USER.fullName
    };

    await updateDebtor(debtorId, {
      assets: (debtor.assets || []).filter(a => a.id !== assetId),
      notes: [newLog, ...(debtor.notes || [])]
    });
  };

  const handleUpdateStatus = async (debtorId, newStatus) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    const oldStatusLabel = settings.find(s => s.key === debtor.status)?.label;
    const newStatusLabel = settings.find(s => s.key === newStatus)?.label;
    
    const newLog = { 
      id: Date.now(), 
      type: 'SYSTEM_LOG', 
      content: `Sistem: Statü "${oldStatusLabel}" -> "${newStatusLabel}" olarak değiştirildi.`, 
      date: new Date().toISOString(),
      author: CURRENT_USER.fullName
    };

    await updateDebtor(debtorId, {
      status: newStatus,
      notes: [newLog, ...(debtor.notes || [])]
    });
  };

  // Tebligat silme
  const handleDeleteNotification = async (debtorId, field) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;
    
    if (!window.confirm('Bu tebligat kaydını silmek istediğinize emin misiniz?')) return;

    const fieldLabel = field === 'notificationDate' ? 'Tebligat' : 'Muhtar Tebligat';
    const oldValue = debtor[field];
    
    // Silinen log kaydı ekle
    addDeletedLog('Tebligat', `${fieldLabel} tarihi silindi`, {
      debtorName: debtor.fullName,
      fileNumber: debtor.fileNumber,
      date: formatDate(oldValue)
    });

    const newLog = { 
      id: Date.now(), 
      type: 'SYSTEM_LOG', 
      content: `Sistem: ${fieldLabel} tarihi silindi. (Eski: ${formatDate(oldValue)})`, 
      date: new Date().toISOString(),
      author: CURRENT_USER.fullName
    };

    await updateDebtor(debtorId, {
      [field]: null,
      notes: [newLog, ...(debtor.notes || [])]
    });
  };

  // Reddiyat düzenleme
  const handleEditRemittance = async (debtorId, remittanceId, newAmount, newNote) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    const updatedRemittances = (debtor.remittances || []).map(r => {
      if (r.id === remittanceId) {
        return { ...r, amount: parseFloat(newAmount), note: newNote, editedAt: new Date().toISOString(), editedBy: CURRENT_USER.fullName };
      }
      return r;
    });

    const newLog = { 
      id: Date.now(), 
      type: 'SYSTEM_LOG', 
      content: `Sistem: Reddiyat kaydı düzenlendi. Yeni tutar: ${formatCurrency(newAmount)}`, 
      date: new Date().toISOString(),
      author: CURRENT_USER.fullName
    };

    await updateDebtor(debtorId, {
      remittances: updatedRemittances,
      notes: [newLog, ...(debtor.notes || [])]
    });
  };

  // Reddiyat silme
  const handleDeleteRemittance = async (debtorId, remittanceId) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    if (!window.confirm('Bu reddiyat kaydını silmek istediğinize emin misiniz?')) return;

    const remittance = (debtor.remittances || []).find(r => r.id === remittanceId);
    if (!remittance) return;

    // Silinen log kaydı ekle
    addDeletedLog('Reddiyat', `${formatCurrency(remittance.amount)} tutarında reddiyat silindi`, {
      debtorName: debtor.fullName,
      fileNumber: debtor.fileNumber,
      amount: remittance.amount,
      note: remittance.note,
      date: remittance.date
    });

    const updatedRemittances = (debtor.remittances || []).filter(r => r.id !== remittanceId);

    const newLog = { 
      id: Date.now(), 
      type: 'SYSTEM_LOG', 
      content: `Sistem: Reddiyat kaydı silindi. (${formatCurrency(remittance.amount)})`, 
      date: new Date().toISOString(),
      author: CURRENT_USER.fullName
    };

    await updateDebtor(debtorId, {
      remittances: updatedRemittances,
      notes: [newLog, ...(debtor.notes || [])]
    });
  };

  const handleUpdateDate = async (debtorId, field, value, promiseAmount = null) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    let logContent = '';
    let updates = { [field]: value || null };
    
    if (field === 'notificationDate') logContent = `Sistem: Tebligat tarihi ${formatDate(value)} olarak girildi.`;
    else if (field === 'mukhtarDate') logContent = `Sistem: Muhtar tebligat tarihi ${formatDate(value)} olarak girildi.`;
    else if (field === 'paymentPromiseDate') {
      if (promiseAmount) {
        updates.paymentPromiseAmount = parseFloat(promiseAmount);
        logContent = `Sistem: Ödeme sözü ${formatDate(value)} tarihinde ${formatCurrency(promiseAmount)} olarak güncellendi.`;
      } else {
        logContent = `Sistem: Ödeme sözü tarihi ${formatDate(value)} olarak güncellendi.`;
      }
    }
    
    const newLog = { 
      id: Date.now(), 
      type: 'SYSTEM_LOG', 
      content: logContent, 
      date: new Date().toISOString(),
      author: CURRENT_USER.fullName
    };

    await updateDebtor(debtorId, {
      ...updates,
      notes: [newLog, ...(debtor.notes || [])]
    });
  };

  const handleAddNote = async (debtorId, content) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    const newNote = {
      id: Date.now(),
      type: 'USER_NOTE',
      content,
      date: new Date().toISOString(),
      author: CURRENT_USER.fullName
    };
    
    await updateDebtor(debtorId, {
      notes: [newNote, ...(debtor.notes || [])]
    });
  };

  const handleEditNote = async (debtorId, noteId, newContent) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    const updatedNotes = (debtor.notes || []).map(n => 
      n.id === noteId ? { ...n, content: newContent, updatedAt: new Date().toISOString() } : n
    );
    
    await updateDebtor(debtorId, { notes: updatedNotes });
  };

  const handleCloseFile = async (debtorId) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) return;

    if (debtor.balance > 0) {
      alert(`HATA: Dosya kapatılamaz! ${formatCurrency(debtor.balance)} kalan borç bulunmaktadır.`);
      return;
    }
    
    if(!window.confirm("Dosyayı kapatmak istediğinize emin misiniz?")) return;

    const newLog = { id: Date.now(), type: 'SYSTEM_LOG', content: 'Sistem: Dosya kapatıldı.', date: new Date().toISOString(), author: CURRENT_USER.fullName };
    
    await updateDebtor(debtorId, {
      isClosed: true,
      status: 'KAPALI',
      notes: [newLog, ...(debtor.notes || [])]
    });
  };

  const getStatusColor = (statusKey) => {
    const setting = settings.find(s => s.key === statusKey);
    return setting ? setting.value : '#94a3b8';
  };

  const getStatusLabel = (statusKey) => {
    const setting = settings.find(s => s.key === statusKey);
    return setting ? setting.label : statusKey;
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const handleNavigate = (newView) => {
    setView(newView);
    setSidebarOpen(false);
  };

  return (
    <div className={`flex h-screen font-sans ${darkMode ? 'dark bg-slate-900 text-slate-100' : 'bg-gradient-to-br from-slate-50 to-slate-100 text-slate-800'}`}>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Modern White/Dark Theme */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-72 lg:w-64 
        ${darkMode ? 'bg-slate-800 text-slate-100 border-slate-700' : 'bg-white text-slate-800 border-slate-100'}
        flex flex-col 
        shadow-[0_0_40px_rgba(0,0,0,0.08)] 
        border-r
        transform transition-transform duration-300 ease-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className={`p-5 border-b ${darkMode ? 'border-slate-700 bg-gradient-to-r from-slate-800 to-slate-900' : 'border-slate-100 bg-gradient-to-r from-slate-50 to-white'}`}>
          <div className="flex items-center gap-3">
            {/* Dark mode'da logoLight (açık renkli), light mode'da logoDark (koyu renkli) kullan */}
            {(darkMode ? config.logoLight : config.logoDark) ? (
              /* Logo varsa sadece logo göster, metin gösterme */
              <img 
                src={darkMode ? config.logoLight : config.logoDark} 
                alt="Logo" 
                className="h-12 max-w-[180px] object-contain" 
              />
            ) : (
              /* Logo yoksa ikon ve metin göster - siteTitle yüklenmişse */
              config.siteTitle && (
                <>
                  <div 
                    className="w-11 h-11 rounded-xl flex items-center justify-center shadow-lg"
                    style={{ 
                      backgroundColor: config.primaryColor || '#f59e0b',
                      boxShadow: `0 4px 14px ${config.primaryColor || '#f59e0b'}40`
                    }}
                  >
                    <Gavel className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className={`font-bold text-base tracking-tight ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                      {config.siteTitle}
                    </h1>
                    <p className={`text-xs tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {config.siteSubtitle}
                    </p>
                  </div>
                </>
              )
            )}
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto scrollbar-thin bg-gradient-to-b from-white to-slate-50/50 dark:from-slate-800 dark:to-slate-900">
          <SidebarItem icon={<FileText />} label="Genel Bakış" active={view === 'dashboard'} onClick={() => handleNavigate('dashboard')} primaryColor={config.primaryColor} />
          <SidebarItem icon={<Users />} label="Borçlu Dosyaları" active={view === 'debtors'} onClick={() => handleNavigate('debtors')} primaryColor={config.primaryColor} />
          <SidebarItem icon={<FolderOpen />} label="Dosya Detayları" active={view === 'fileOperations'} onClick={() => handleNavigate('fileOperations')} primaryColor={config.primaryColor} />
          <SidebarItem icon={<Calendar />} label="Takvim" active={view === 'calendar'} onClick={() => handleNavigate('calendar')} primaryColor={config.primaryColor} />
          <SidebarItem icon={<Bell />} label="Tebligat Takibi" active={view === 'notifications'} onClick={() => handleNavigate('notifications')} primaryColor={config.primaryColor} />
          <SidebarItem icon={<Wallet />} label="Kasa & Alacaklı" active={view === 'remittances'} onClick={() => handleNavigate('remittances')} primaryColor={config.primaryColor} />
          <SidebarItem icon={<CreditCard />} label="Ödeme Ekle" active={view === 'payment'} onClick={() => handleNavigate('payment')} primaryColor={config.primaryColor} />
          <SidebarItem icon={<Phone />} label="Görüşmeler" active={view === 'interactions'} onClick={() => handleNavigate('interactions')} primaryColor={config.primaryColor} />
          <SidebarItem icon={<BarChart3 />} label="Raporlar" active={view === 'reports'} onClick={() => handleNavigate('reports')} primaryColor={config.primaryColor} />
          <SidebarItem icon={<Trash2 />} label="Silinenler" active={view === 'deleted'} onClick={() => handleNavigate('deleted')} primaryColor={config.primaryColor} />
          <SidebarItem icon={<Settings />} label="Ayarlar" active={view === 'settings'} onClick={() => handleNavigate('settings')} primaryColor={config.primaryColor} />
        </nav>

        <div className="p-4 border-t border-slate-100 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
          {/* Dark Mode Toggle */}
          <button 
            onClick={() => setDarkMode(!darkMode)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all border border-slate-200 dark:border-slate-600"
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            <span className="text-sm font-medium">{darkMode ? 'Açık Tema' : 'Koyu Tema'}</span>
          </button>
          
          <button 
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-slate-500 dark:text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-all shadow-sm border border-slate-200 dark:border-slate-600 hover:border-red-200"
          >
            <LogOut size={18} />
            <span className="text-sm font-medium">Çıkış Yap</span>
          </button>
          <div className="text-xs text-slate-400 dark:text-slate-500 text-center mt-3 font-medium">
            <span className="text-red-500">♥</span> AMC Fikir Sanat | İCRA TAKİP YAZILIMI V1.5
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 overflow-auto flex flex-col min-w-0 ${darkMode ? 'bg-slate-900' : ''}`} style={{ minHeight: '100vh' }}>
        <header className={`${darkMode ? 'bg-slate-800/80 border-slate-700/50' : 'bg-white/80 border-slate-200/50'} backdrop-blur-lg shadow-sm px-4 sm:px-6 py-4 flex justify-between items-center sticky top-0 z-20 border-b`}>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(true)}
              className={`lg:hidden p-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'} rounded-xl transition-colors`}
            >
              <Menu size={24} className={darkMode ? 'text-slate-300' : 'text-slate-600'} />
            </button>
            <h1 className={`text-lg sm:text-xl lg:text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'} truncate`}>
              {view === 'dashboard' && 'Yönetim Paneli'}
              {view === 'debtors' && 'Borçlu Takip Listesi'}
              {view === 'fileOperations' && 'Dosya Detayları'}
              {view === 'calendar' && 'Ödeme Sözü Takvimi'}
              {view === 'notifications' && 'Tebligat Takibi'}
              {view === 'remittances' && 'Kasa & Alacaklı'}
              {view === 'payment' && 'Tahsilat İşlemleri'}
              {view === 'interactions' && 'Görüşme Kayıtları'}
              {view === 'settings' && 'Sistem Ayarları'}
              {view === 'reports' && 'Raporlar'}
              {view === 'deleted' && 'Silinenler'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-right hidden sm:block">
              <div className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{CURRENT_USER.fullName}</div>
              <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{CURRENT_USER.role === 'ADMIN' ? 'Yönetici' : CURRENT_USER.role === 'LAWYER' ? 'Avukat' : 'Personel'}</div>
            </div>
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shadow-lg"
              style={{ 
                background: `linear-gradient(135deg, ${config.primaryColor || '#f59e0b'} 0%, ${config.primaryColor || '#f59e0b'}dd 100%)`
              }}
            >
              {CURRENT_USER.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 pb-20 flex-1 overflow-x-hidden">
          {view === 'dashboard' && <DashboardView stats={stats} debtors={debtors} getStatusColor={getStatusColor} getStatusLabel={getStatusLabel} />}
          {view === 'debtors' && (
            <DebtorsView 
              debtors={debtors} 
              settings={settings}
              searchTerm={searchTerm} 
              setSearchTerm={setSearchTerm} 
              onSelectDebtor={(d) => { setSelectedDebtor(d); setIsModalOpen(true); }}
              onAddDebtorClick={() => { setEditingDebtor(null); setIsAddDebtorModalOpen(true); }}
              onEditDebtorClick={handleEditClick}
              onUpdateStatus={handleUpdateStatus}
              getStatusColor={getStatusColor}
              getStatusLabel={getStatusLabel}
            />
          )}
          {view === 'fileOperations' && (
            <FileOperationsView
              debtors={debtors}
              settings={settings}
              onAddPayment={handleAddPayment}
              onUpdateStatus={handleUpdateStatus}
              onAddNote={handleAddNote}
              onCloseFile={handleCloseFile}
              onEditNote={handleEditNote}
              onUpdateDate={handleUpdateDate}
              onAddAsset={handleAddAsset}
              onRemoveAsset={handleRemoveAsset}
              getStatusColor={getStatusColor}
              getStatusLabel={getStatusLabel}
            />
          )}
          {view === 'calendar' && (
            <CalendarView 
              debtors={debtors}
              getStatusColor={getStatusColor}
              getStatusLabel={getStatusLabel}
            />
          )}
          {view === 'notifications' && <NotificationsView debtors={debtors} onUpdateDate={handleUpdateDate} onDeleteNotification={handleDeleteNotification} addDeletedLog={addDeletedLog} />}
          {view === 'remittances' && <RemittancesView debtors={debtors} onAddRemittance={handleAddRemittance} onEditRemittance={handleEditRemittance} onDeleteRemittance={handleDeleteRemittance} addDeletedLog={addDeletedLog} />}
          {view === 'payment' && <PaymentView debtors={debtors} onAddPayment={handleAddPayment} />}
          {view === 'interactions' && <InteractionsView debtors={debtors} onAddInteraction={handleAddInteraction} />}
          {view === 'settings' && <SettingsView settings={settings} setSettings={setSettings} users={users} setUsers={setUsers} />}
          {view === 'reports' && <ReportsView debtors={debtors} settings={settings} />}
          {view === 'deleted' && <DeletedLogsView deletedLogs={deletedLogs} />}
        </div>
      </main>

      {/* Modals */}
      {isModalOpen && selectedDebtor && (
        <DebtorDetailModal 
          debtor={selectedDebtor} 
          onClose={() => { setIsModalOpen(false); setSelectedDebtor(null); }}
          settings={settings}
          onAddPayment={handleAddPayment}
          onUpdateStatus={handleUpdateStatus}
          onAddNote={handleAddNote}
          onCloseFile={handleCloseFile}
          onEditNote={handleEditNote}
          onUpdateDate={handleUpdateDate}
          onAddAsset={handleAddAsset}
          onRemoveAsset={handleRemoveAsset}
          getStatusLabel={getStatusLabel}
          getStatusColor={getStatusColor}
        />
      )}

      {isAddDebtorModalOpen && (
        <AddDebtorModal 
          onClose={() => { setIsAddDebtorModalOpen(false); setEditingDebtor(null); }} 
          onSave={handleSaveDebtor}
          settings={settings}
          debtorToEdit={editingDebtor}
        />
      )}
    </div>
  );
}

// --- VIEWS ---

function DashboardView({ stats, debtors, getStatusColor, getStatusLabel }) {
  const activeNotifications = debtors.filter(d => !d.isClosed && (d.notificationDate || d.mukhtarDate)).map(d => {
    const { isFinalized, daysLeft } = calculateFinalized(d.notificationDate, d.mukhtarDate);
    return { ...d, isFinalized, daysLeft };
  }).sort((a, b) => a.daysLeft - b.daysLeft);

  // Ödeme sözü olan borçlular (tarih alanı dolu olanlar)
  const debtorsWithPromise = debtors.filter(d => !d.isClosed && d.paymentPromiseDate).map(d => {
    const { daysLeft, isOverdue } = calculatePromiseCountdown(d.paymentPromiseDate);
    return { ...d, promiseDaysLeft: daysLeft, promiseOverdue: isOverdue };
  }).sort((a, b) => a.promiseDaysLeft - b.promiseDaysLeft);

  // Son 5 görüşme (INTERACTION veya USER_NOTE tipindeki notlar)
  const recentInteractions = debtors
    .flatMap(d => (d.notes || [])
      .filter(n => n.type === 'INTERACTION' || n.type === 'USER_NOTE')
      .map(n => ({ ...n, debtorName: d.fullName, fileNo: d.fileNumber }))
    )
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* İstatistik Kartları - 1. Satır: 3'lü */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={<FileText />} title="Toplam Dosya" value={stats.totalFiles} color="bg-blue-500" />
        <StatCard icon={<Gavel />} title="Aktif Dosyalar" value={stats.activeFiles} color="bg-amber-500" />
        <StatCard icon={<CalendarCheck />} title="Toplam Ödeme Sözü" value={debtorsWithPromise.length} color="bg-purple-500" />
      </div>

      {/* İstatistik Kartları - 2. Satır: 2'li */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard icon={<DollarSign />} title="Toplam Alacak" value={formatCurrency(stats.totalDebt)} color="bg-emerald-500" />
        <StatCard icon={<CheckCircle />} title="Toplam Tahsilat" value={formatCurrency(stats.totalPaid)} color="bg-indigo-500" />
      </div>

      {/* Ödeme Sözü Detay Bölümü */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h3 className="font-bold text-lg mb-4 text-slate-800 flex items-center gap-2">
          <CalendarCheck className="w-5 h-5 text-purple-500" /> Ödeme Sözü Takibi
        </h3>
        <div className="overflow-auto max-h-[300px]">
          {debtorsWithPromise.length === 0 ? (
            <div className="text-center text-slate-400 p-4">Ödeme sözü bulunan dosya yok.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-600">Dosya No</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Borçlu</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Söz Tarihi</th>
                  <th className="text-right p-3 font-semibold text-slate-600">Söz Tutarı</th>
                  <th className="text-right p-3 font-semibold text-slate-600">Kalan Gün</th>
                </tr>
              </thead>
              <tbody>
                {debtorsWithPromise.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-700">{d.fileNumber}</td>
                    <td className="p-3 text-slate-600">{d.fullName}</td>
                    <td className="p-3 text-slate-600">{formatDate(d.paymentPromiseDate)}</td>
                    <td className="p-3 text-right font-semibold text-emerald-600">
                      {d.paymentPromiseAmount ? formatCurrency(d.paymentPromiseAmount) : '-'}
                    </td>
                    <td className="p-3 text-right">
                      {d.promiseOverdue ? (
                        <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">
                          <AlertTriangle size={12} /> {Math.abs(d.promiseDaysLeft)} gün geçti
                        </span>
                      ) : d.promiseDaysLeft === 0 ? (
                        <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded text-xs font-bold">
                          Bugün
                        </span>
                      ) : (
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold">
                          {d.promiseDaysLeft} gün
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="flex flex-col gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex flex-col">
            <h3 className="font-bold text-lg mb-4 text-slate-800 flex items-center gap-2">
              <Bell className="w-5 h-5 text-red-500" /> Kesinleşme Takibi (Kritik)
            </h3>
            <div className="space-y-0 flex-1 overflow-auto max-h-[250px]">
              {activeNotifications.length === 0 && <div className="text-center text-slate-400 p-4">Takip edilecek tebligat bulunamadı.</div>}
              {activeNotifications.map((d) => (
                <div key={d.id} className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <div>
                    <span className="font-bold text-slate-700 block text-sm">{d.fullName} ({d.fileNumber})</span>
                    <span className="text-xs text-slate-500">Tebliğ: {formatDate(d.notificationDate || d.mukhtarDate)}</span>
                  </div>
                  <div>
                    {d.isFinalized ? (
                      <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">
                        <AlertTriangle size={12} /> Kesinleşti (+7 Gün)
                      </span>
                    ) : (
                      <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded text-xs font-bold">
                        {Math.abs(d.daysLeft)} Gün Kaldı
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Son 5 Görüşme */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex flex-col">
            <h3 className="font-bold text-lg mb-4 text-slate-800 flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-500" /> Son 5 Görüşme
            </h3>
            <div className="space-y-0 flex-1 overflow-auto max-h-[250px]">
              {recentInteractions.length === 0 ? (
                <div className="text-center text-slate-400 p-4">Henüz görüşme kaydı bulunmuyor.</div>
              ) : (
                recentInteractions.map((log, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-sm p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                    <div className="mt-1 min-w-[8px] h-2 rounded-full bg-blue-400" />
                    <div className="flex-1">
                      <span className="font-semibold text-slate-700 block">{log.debtorName} ({log.fileNo})</span>
                      <p className="text-slate-600 mt-0.5 line-clamp-2">{log.content}</p>
                      <span className="text-xs text-slate-400 block mt-1">
                        {new Date(log.date).toLocaleString('tr-TR')} • {log.author || 'Sistem'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          {/* Son Hareketler */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex flex-col">
            <h3 className="font-bold text-lg mb-4 text-slate-800 flex items-center gap-2">
              <History className="w-5 h-5 text-slate-400" /> Son Hareketler
            </h3>
            <div className="space-y-0 flex-1 overflow-auto max-h-[250px]">
              {debtors.flatMap(d => (d.notes || []).map(n => ({...n, debtorName: d.fullName, fileNo: d.fileNumber})))
                .sort((a,b) => new Date(b.date) - new Date(a.date))
                .slice(0, 5) 
                .map((log, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-sm p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                    <div className={`mt-1 min-w-[8px] h-2 rounded-full ${log.type === 'SYSTEM_LOG' ? 'bg-amber-400' : 'bg-blue-400'}`} />
                    <div className="flex-1">
                      <span className="font-semibold text-slate-700 block">{log.debtorName} ({log.fileNo})</span>
                      <p className="text-slate-600 mt-0.5 line-clamp-2">{log.content}</p>
                      <span className="text-xs text-slate-400 block mt-1">
                        {new Date(log.date).toLocaleString('tr-TR')} • {log.author || 'Sistem'}
                      </span>
                    </div>
                  </div>
                ))
              }
              {debtors.flatMap(d => d.notes || []).length === 0 && <div className="text-center text-slate-400 p-4">Hareket bulunamadı.</div>}
            </div>
          </div>

          {/* Dosya Durum Dağılımı */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h3 className="font-bold text-lg mb-4 text-slate-800">Dosya Durum Dağılımı</h3>
            <div className="space-y-3 max-h-[250px] overflow-auto pr-2">
              {Object.entries(debtors.reduce((acc, curr) => {
                acc[curr.status] = (acc[curr.status] || 0) + 1;
                return acc;
              }, {})).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getStatusColor(status) }}></div>
                    <span className="font-medium text-slate-700">{getStatusLabel(status)}</span>
                  </div>
                  <span className="font-bold text-slate-900 bg-white px-2 py-1 rounded shadow-sm border">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DebtorsView({ debtors, searchTerm, setSearchTerm, onSelectDebtor, onAddDebtorClick, onEditDebtorClick, onUpdateStatus, getStatusColor, getStatusLabel, settings }) {
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterNotification, setFilterNotification] = useState('ALL');

  const filteredDebtors = debtors.filter(d => {
    const matchesSearch = d.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) || d.fileNumber?.includes(searchTerm);
    const matchesStatus = filterStatus === 'ALL' || d.status === filterStatus;
    const matchesNotification = filterNotification === 'ALL' 
      ? true 
      : filterNotification === 'YES' ? d.notificationDate : !d.notificationDate;

    return matchesSearch && matchesStatus && matchesNotification;
  });

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-[calc(100vh-140px)]">
      <div className="p-4 border-b border-slate-200 flex flex-col lg:flex-row gap-4 justify-between items-center bg-slate-50/50 rounded-t-xl">
        <div className="flex flex-col md:flex-row gap-3 w-full lg:w-auto flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 text-slate-400 w-5 h-5" />
            <input 
              type="text" 
              placeholder="Dosya No veya İsim..." 
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="debtor-search-input"
            />
          </div>
          
          <select 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
          >
            <option value="ALL">Tüm Durumlar</option>
            {settings.filter(s => s.category === 'STATUS_COLOR').map(s => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>

          <select 
            value={filterNotification}
            onChange={(e) => setFilterNotification(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
          >
            <option value="ALL">Tebligat (Tümü)</option>
            <option value="YES">Tebligat Yapıldı</option>
            <option value="NO">Tebligat Yok</option>
          </select>
        </div>

        <button 
          onClick={onAddDebtorClick}
          className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap"
          data-testid="add-debtor-btn"
        >
          <Plus size={18} />
          <span>Yeni Dosya Ekle</span>
        </button>
      </div>

      <div className="overflow-auto flex-1">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-100 sticky top-0 z-10 text-xs font-bold text-slate-500 uppercase tracking-wider">
            <tr>
              <th className="p-4 border-b">Dosya No</th>
              <th className="p-4 border-b">Borçlu Adı</th>
              <th className="p-4 border-b text-right">Toplam Borç</th>
              <th className="p-4 border-b text-right">Kalan Bakiye</th>
              <th className="p-4 border-b text-center">Durum</th>
              <th className="p-4 border-b text-center">Tebligat</th>
              <th className="p-4 border-b"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredDebtors.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-8 text-center text-slate-500">
                  Kriterlere uygun kayıt bulunamadı.
                </td>
              </tr>
            ) : filteredDebtors.map(debtor => {
              const { isFinalized } = calculateFinalized(debtor.notificationDate, debtor.mukhtarDate);
              return (
                <tr key={debtor.id} className="hover:bg-blue-50/30 transition-colors group">
                  <td className="p-4 font-medium text-slate-700">{debtor.fileNumber}</td>
                  <td className="p-4">
                    <div className="font-semibold text-slate-800">{debtor.fullName}</div>
                    <div className="text-xs text-slate-500">{debtor.tcKimlik}</div>
                  </td>
                  <td className="p-4 text-right font-mono text-slate-600">{formatCurrency(debtor.totalDebt)}</td>
                  <td className="p-4 text-right font-mono font-bold text-slate-900">{formatCurrency(debtor.balance)}</td>
                  <td className="p-4 text-center">
                    <div className="relative inline-block">
                      <select 
                        value={debtor.status}
                        onChange={(e) => onUpdateStatus(debtor.id, e.target.value)}
                        className="appearance-none text-center px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-sm cursor-pointer focus:ring-2 focus:ring-offset-1 focus:ring-amber-500 outline-none hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: getStatusColor(debtor.status) }}
                      >
                        {settings.filter(s => s.category === 'STATUS_COLOR').map(s => (
                          <option key={s.key} value={s.key} style={{ color: '#000', backgroundColor: '#fff' }}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    {isFinalized ? (
                      <div className="inline-flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded text-xs font-bold border border-red-100">
                        <AlertTriangle size={12} /> Kesinleşti
                      </div>
                    ) : debtor.notificationDate ? (
                      <span className="text-xs text-slate-500">Süre İşliyor</span>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={(e) => { e.stopPropagation(); onEditDebtorClick(debtor); }}
                        className="text-slate-400 hover:text-blue-600 p-2 rounded-full hover:bg-blue-50 transition-all"
                        title="Düzenle"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button 
                        onClick={() => onSelectDebtor(debtor)}
                        className="text-slate-400 hover:text-amber-600 p-2 rounded-full hover:bg-amber-50 transition-all"
                        title="Detaylar"
                      >
                        <MoreVertical size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Debtor Detail Content (shared between modal and page)
function DebtorDetailContent({ debtor, settings, onAddPayment, onUpdateStatus, onAddNote, onCloseFile, onEditNote, onUpdateDate, onAddAsset, onRemoveAsset, getStatusLabel, getStatusColor }) {
  const [payAmount, setPayAmount] = useState('');
  const [payType, setPayType] = useState('Nakit');
  const [noteContent, setNoteContent] = useState('');
  const [activeTab, setActiveTab] = useState('info'); 
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  
  // Söz tarihi ve tutarı için state
  const [promiseDate, setPromiseDate] = useState(debtor?.paymentPromiseDate || '');
  const [promiseAmount, setPromiseAmount] = useState(debtor?.paymentPromiseAmount || '');
  
  // Mal varlığı ekleme için state
  const [newAssetType, setNewAssetType] = useState('Gayrimenkul');
  const [newAssetDescription, setNewAssetDescription] = useState('');

  // Debtor değiştiğinde state'leri güncelle
  useEffect(() => {
    setPromiseDate(debtor?.paymentPromiseDate || '');
    setPromiseAmount(debtor?.paymentPromiseAmount || '');
  }, [debtor?.paymentPromiseDate, debtor?.paymentPromiseAmount]);

  const { isFinalized, daysLeft } = calculateFinalized(debtor?.notificationDate, debtor?.mukhtarDate);
  const { daysLeft: promiseDaysLeft, isOverdue } = calculatePromiseCountdown(debtor?.paymentPromiseDate);
  const totalLiability = (debtor.totalDebt || 0) + (debtor.costAmount || 0);
  const percentage = totalLiability > 0 ? ((debtor.totalPaid || 0) / totalLiability) * 100 : 0;

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.content);
  };

  const saveEditedNote = () => {
    onEditNote(debtor.id, editingNoteId, editingNoteText);
    setEditingNoteId(null);
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Status Bar */}
      <div className="bg-slate-100 p-4 border-b flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-600">Durum:</span>
            <select 
              value={debtor.status} 
              onChange={(e) => onUpdateStatus(debtor.id, e.target.value)}
              disabled={debtor.isClosed}
              className="appearance-none text-center px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-sm cursor-pointer focus:ring-2 focus:ring-offset-1 focus:ring-amber-500 outline-none hover:opacity-90 transition-opacity"
              style={{ backgroundColor: getStatusColor(debtor.status) }}
            >
              {settings.filter(s => s.category === 'STATUS_COLOR').map(s => (
                <option key={s.key} value={s.key} style={{ color: '#000', backgroundColor: '#fff' }}>{s.label}</option>
              ))}
            </select>
          </div>

          {debtor.status === 'ODEME' && (
            <div className="flex items-center gap-2 border-l pl-4 border-slate-300 flex-wrap">
              <span className="text-sm font-semibold text-slate-600 flex items-center gap-1">
                <Timer size={14} className="text-amber-600" /> Söz:
              </span>
              <input 
                type="date" 
                value={promiseDate} 
                onChange={(e) => setPromiseDate(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1 text-sm outline-none focus:border-amber-500 bg-white shadow-sm"
              />
              <input 
                type="number" 
                value={promiseAmount} 
                onChange={(e) => setPromiseAmount(e.target.value)}
                placeholder="Tutar (TL)"
                className="border border-slate-300 rounded px-2 py-1 text-sm outline-none focus:border-amber-500 bg-white shadow-sm w-28"
              />
              <button 
                onClick={() => {
                  if (promiseDate) {
                    onUpdateDate(debtor.id, 'paymentPromiseDate', promiseDate, promiseAmount);
                  }
                }}
                className="bg-amber-500 text-white px-3 py-1 rounded text-sm hover:bg-amber-600 transition-colors"
              >
                Kaydet
              </button>
              {debtor.paymentPromiseDate && (
                <span className={`text-xs font-bold px-2 py-1 rounded shadow-sm ${isOverdue ? 'bg-red-100 text-red-600 border border-red-200' : 'bg-green-100 text-green-600 border border-green-200'}`}>
                  {isOverdue ? `${Math.abs(promiseDaysLeft)} Gün Gecikti!` : `${promiseDaysLeft} Gün Kaldı`}
                  {debtor.paymentPromiseAmount && ` • ${formatCurrency(debtor.paymentPromiseAmount)}`}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-4">
          {isFinalized && <span className="text-red-600 text-xs font-bold bg-red-100 px-2 py-1 rounded border border-red-200 flex items-center gap-1"><AlertTriangle size={12}/> Takip Kesinleşti</span>}
          {debtor.notificationDate && !isFinalized && <span className="text-amber-600 text-xs bg-amber-50 px-2 py-1 rounded border border-amber-200">Kesinleşmeye {Math.abs(daysLeft)} gün kaldı</span>}
        </div>
        <div className="font-mono font-bold text-lg text-slate-800">
          Kalan: {formatCurrency(debtor.balance)}
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row pb-8">
        <div className="w-full md:w-2/3 border-r border-slate-100 p-6">
          <div className="flex gap-4 border-b border-slate-200 mb-6 overflow-x-auto">
            <button onClick={() => setActiveTab('info')} className={`pb-2 text-sm font-medium whitespace-nowrap ${activeTab === 'info' ? 'text-amber-600 border-b-2 border-amber-500' : 'text-slate-500'}`}>Dosya Bilgileri</button>
            <button onClick={() => setActiveTab('payment')} className={`pb-2 text-sm font-medium whitespace-nowrap ${activeTab === 'payment' ? 'text-amber-600 border-b-2 border-amber-500' : 'text-slate-500'}`}>Ödeme Al</button>
            <button onClick={() => setActiveTab('assets')} className={`pb-2 text-sm font-medium whitespace-nowrap ${activeTab === 'assets' ? 'text-amber-600 border-b-2 border-amber-500' : 'text-slate-500'}`}>Mal Varlığı</button>
            <button onClick={() => setActiveTab('history')} className={`pb-2 text-sm font-medium whitespace-nowrap ${activeTab === 'history' ? 'text-amber-600 border-b-2 border-amber-500' : 'text-slate-500'}`}>Tarihçe & Notlar</button>
          </div>

          {activeTab === 'info' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded border border-slate-100">
                  <div className="text-xs text-slate-500">Ana Para + Faiz</div>
                  <div className="font-semibold">{formatCurrency(debtor.totalDebt)}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded border border-slate-100">
                  <div className="text-xs text-slate-500">Masraflar</div>
                  <div className="font-semibold">{formatCurrency(debtor.costAmount)}</div>
                </div>
                <div className="p-3 bg-green-50 rounded border border-green-100">
                  <div className="text-xs text-green-600">Toplam Tahsilat</div>
                  <div className="font-bold text-green-700">{formatCurrency(debtor.totalPaid)}</div>
                </div>
                <div className="p-3 bg-blue-50 rounded border border-blue-100">
                  <div className="text-xs text-blue-600">Alacaklıya Ödenen</div>
                  <div className="font-bold text-blue-700">{formatCurrency(debtor.remittedAmount || 0)}</div>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="font-semibold text-sm mb-2 text-slate-700">Tarihler</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-slate-500 w-24 inline-block">Tebligat:</span> {formatDate(debtor.notificationDate)}</div>
                  <div><span className="text-slate-500 w-24 inline-block">Muhtar:</span> {formatDate(debtor.mukhtarDate)}</div>
                  <div><span className="text-slate-500 w-24 inline-block">Açılış:</span> {formatDate(debtor.createdAt)}</div>
                </div>
              </div>

              {!debtor.isClosed && (
                <div className="mt-8 p-4 bg-red-50 rounded border border-red-100 flex items-center justify-between">
                  <span className="text-sm text-red-800 font-medium">Dosya Bakiyesi: {formatCurrency(debtor.balance)}</span>
                  <button onClick={() => onCloseFile(debtor.id)} className="bg-white border border-red-300 text-red-700 px-3 py-1.5 rounded text-sm hover:bg-red-600 hover:text-white transition-colors">
                    Dosyayı Kapat
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'payment' && (
            <div className="space-y-4">
              {debtor.isClosed ? <div className="text-center text-red-500 font-bold p-4">Dosya kapalı, işlem yapılamaz.</div> : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Miktar (TL)</label>
                    <input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="w-full border p-2 rounded focus:ring-amber-500" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Ödeme Türü</label>
                    <select value={payType} onChange={(e) => setPayType(e.target.value)} className="w-full border p-2 rounded">
                      <option>Nakit</option>
                      <option>Kredi Kartı</option>
                      <option>Havale/EFT</option>
                      <option>Maaş Haczi</option>
                    </select>
                  </div>
                  <button 
                    onClick={() => {
                      if(!payAmount) return;
                      onAddPayment(debtor.id, payAmount, payType, 'Detay ekranından eklendi');
                      setPayAmount('');
                      setActiveTab('history');
                    }}
                    className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 flex justify-center items-center gap-2"
                  >
                    <DollarSign size={18} /> Tahsilat Ekle
                  </button>
                </>
              )}
            </div>
          )}

          {activeTab === 'assets' && (
            <div className="h-full flex flex-col">
              {/* Mal Varlığı Ekleme Formu */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-sm text-slate-700 mb-3">Yeni Mal Varlığı Ekle</h4>
                <div className="flex flex-col sm:flex-row gap-3">
                  <select 
                    value={newAssetType} 
                    onChange={(e) => setNewAssetType(e.target.value)}
                    className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="Gayrimenkul">🏠 Gayrimenkul</option>
                    <option value="Araç">🚗 Araç</option>
                    <option value="Banka Hesabı">🏦 Banka Hesabı</option>
                    <option value="Maaş">💰 Maaş/Gelir</option>
                    <option value="Diğer">📦 Diğer</option>
                  </select>
                  <input 
                    type="text" 
                    value={newAssetDescription} 
                    onChange={(e) => setNewAssetDescription(e.target.value)}
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" 
                    placeholder="Açıklama (örn: Erzincan Merkez İnönü Mah. Arsa)" 
                  />
                  <button 
                    onClick={() => {
                      if(!newAssetDescription.trim()) return;
                      onAddAsset(debtor.id, { type: newAssetType, description: newAssetDescription });
                      setNewAssetDescription('');
                    }}
                    className="bg-amber-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-amber-600 flex items-center gap-2 whitespace-nowrap"
                  >
                    <Plus size={16} /> Ekle
                  </button>
                </div>
              </div>

              {/* Mal Varlığı Listesi */}
              <div className="flex-1 overflow-auto">
                {(!debtor.assets || debtor.assets.length === 0) ? (
                  <div className="text-center text-slate-400 text-sm py-8">
                    <Package size={48} className="mx-auto mb-3 opacity-50" />
                    <p>Henüz mal varlığı kaydı yok.</p>
                    <p className="text-xs mt-1">Yukarıdaki formu kullanarak ekleyebilirsiniz.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(debtor.assets || []).map((asset, idx) => (
                      <div key={asset.id || idx} className="p-4 bg-white rounded-lg border border-slate-200 flex items-start justify-between gap-3 hover:shadow-md transition-shadow">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${
                            asset.type === 'Gayrimenkul' ? 'bg-blue-100 text-blue-600' :
                            asset.type === 'Araç' ? 'bg-green-100 text-green-600' :
                            asset.type === 'Banka Hesabı' ? 'bg-purple-100 text-purple-600' :
                            asset.type === 'Maaş' ? 'bg-amber-100 text-amber-600' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {asset.type === 'Gayrimenkul' && <Home size={20} />}
                            {asset.type === 'Araç' && <Car size={20} />}
                            {asset.type === 'Banka Hesabı' && <CreditCard size={20} />}
                            {asset.type === 'Maaş' && <Wallet size={20} />}
                            {asset.type === 'Diğer' && <Package size={20} />}
                          </div>
                          <div>
                            <div className="font-medium text-slate-800">{asset.description}</div>
                            <div className="text-xs text-slate-500 mt-1">
                              {asset.type} • {asset.addedAt ? new Date(asset.addedAt).toLocaleDateString('tr-TR') : '-'}
                            </div>
                          </div>
                        </div>
                        <button 
                          onClick={() => onRemoveAsset(debtor.id, asset.id)}
                          className="text-slate-400 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Sil"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="h-full flex flex-col">
              <div className="mb-4 flex gap-2">
                <input 
                  type="text" 
                  value={noteContent} 
                  onChange={(e) => setNoteContent(e.target.value)}
                  className="flex-1 border p-2 rounded text-sm" 
                  placeholder="Not ekle..." 
                />
                <button 
                  onClick={() => {
                    if(!noteContent) return;
                    onAddNote(debtor.id, noteContent);
                    setNoteContent('');
                  }}
                  className="bg-slate-800 text-white px-3 rounded text-sm hover:bg-slate-700"
                >
                  Ekle
                </button>
              </div>
              <div className="flex-1 overflow-auto space-y-3 pr-2">
                {(!debtor.notes || debtor.notes.length === 0) && <div className="text-center text-slate-400 text-sm mt-4">Henüz kayıt yok.</div>}
                {(debtor.notes || []).map((note) => (
                  <div key={note.id} className={`p-3 rounded-lg text-sm border ${note.type === 'SYSTEM_LOG' ? 'bg-amber-50 border-amber-100' : 'bg-white border-slate-200'}`}>
                    <div className="flex justify-between items-start mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-bold text-xs ${note.type === 'SYSTEM_LOG' ? 'text-amber-700' : 'text-blue-700'}`}>
                          {note.type === 'SYSTEM_LOG' ? 'SİSTEM' : 'KULLANICI'}
                        </span>
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <User size={10} /> {note.author || 'Sistem'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">{new Date(note.date).toLocaleString('tr-TR')}</span>
                        {editingNoteId !== note.id && (
                          <button onClick={() => startEditNote(note)} className="text-slate-300 hover:text-blue-500">
                            <Edit2 size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {editingNoteId === note.id ? (
                      <div className="mt-1 flex gap-2">
                        <input 
                          type="text" 
                          value={editingNoteText} 
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          className="flex-1 border p-1 rounded text-sm"
                        />
                        <button onClick={saveEditedNote} className="text-green-600 text-xs font-bold">Kaydet</button>
                        <button onClick={() => setEditingNoteId(null)} className="text-red-500 text-xs">İptal</button>
                      </div>
                    ) : (
                      <p className="text-slate-700">{note.content}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="w-full md:w-1/3 bg-slate-50 p-6 flex flex-col gap-4">
          <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide border-b border-slate-200 pb-2">Özet</h4>
          
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Dosya No</span>
              <span className="font-medium text-slate-800">{debtor.fileNumber}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Borçlu</span>
              <span className="font-medium text-slate-800 text-right">{debtor.fullName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Durum</span>
              <span className="text-xs px-2 py-0.5 rounded text-white" style={{ backgroundColor: getStatusColor(debtor.status) }}>
                {getStatusLabel(debtor.status)}
              </span>
            </div>
          </div>

          <div className="mt-4">
            <div className="bg-white p-4 rounded border border-slate-200 shadow-sm">
              <div className="text-center">
                <div className="text-xs text-slate-500 mb-1">Tahsilat Oranı</div>
                <div className="w-full bg-slate-100 rounded-full h-2 mb-2 overflow-hidden">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-500" 
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <div className="font-bold text-green-600">
                  %{Math.round(percentage)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FileOperationsView({ debtors, settings, onAddPayment, onUpdateStatus, onAddNote, onCloseFile, onEditNote, onUpdateDate, onAddAsset, onRemoveAsset, getStatusLabel, getStatusColor }) {
  const [selectedDebtorId, setSelectedDebtorId] = useState(null);
  const selectedDebtor = debtors.find(d => d.id === selectedDebtorId);

  const debtorOptions = debtors.filter(d => !d.isClosed).map(d => ({
    value: d.id,
    label: `${d.fileNumber} - ${d.fullName}`
  }));

  if (!selectedDebtor) {
    return (
      <div className="max-w-2xl mx-auto mt-20">
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8 text-center">
          <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <FolderOpen size={40} className="text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Dosya İşlemleri</h2>
          <p className="text-slate-500 mb-8">İşlem yapmak veya detaylarını görüntülemek istediğiniz dosyayı arayın.</p>
          
          <div className="text-left">
            <label className="block text-sm font-medium text-slate-700 mb-2">Dosya Arama</label>
            <SearchableSelect 
              options={debtorOptions}
              value={selectedDebtorId}
              onChange={setSelectedDebtorId}
              placeholder="Dosya No veya Borçlu Adı ile ara..."
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-180px)] bg-white rounded-xl shadow-sm border border-slate-200">
      <div className="bg-white border-b border-slate-200 p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setSelectedDebtorId(null)}
            className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors"
            title="Aramaya Dön"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="font-bold text-lg text-slate-800 flex items-center gap-2">
              {selectedDebtor.fullName} 
              <span className="text-sm font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                {selectedDebtor.fileNumber}
              </span>
            </h2>
            <div className="text-xs text-slate-500 flex gap-3">
              <span>TC: {selectedDebtor.tcKimlik}</span>
              <span>Tel: {selectedDebtor.phone}</span>
            </div>
          </div>
        </div>
        <button 
          onClick={() => setSelectedDebtorId(null)}
          className="text-sm text-slate-500 hover:text-amber-600 font-medium"
        >
          Farklı Dosya Seç
        </button>
      </div>

      <DebtorDetailContent 
        debtor={selectedDebtor}
        settings={settings}
        onAddPayment={onAddPayment}
        onUpdateStatus={onUpdateStatus}
        onAddNote={onAddNote}
        onCloseFile={onCloseFile}
        onEditNote={onEditNote}
        onUpdateDate={onUpdateDate}
        getStatusLabel={getStatusLabel}
        getStatusColor={getStatusColor}
      />
    </div>
  );
}

function DebtorDetailModal({ debtor, onClose, ...props }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] flex flex-col shadow-2xl overflow-auto animate-in fade-in zoom-in duration-200">
        <div className="bg-slate-900 text-white p-6 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold">{debtor.fullName}</h2>
              <span className="px-2 py-0.5 bg-slate-700 rounded text-xs font-mono text-amber-400">{debtor.fileNumber}</span>
              {debtor.isClosed && <span className="px-2 py-0.5 bg-red-600 rounded text-xs font-bold">DOSYA KAPALI</span>}
            </div>
            <p className="text-slate-400 mt-1 flex gap-4 text-sm">
              <span>TC: {debtor.tcKimlik || '-'}</span>
              <span>Tel: {debtor.phone || '-'}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>

        <DebtorDetailContent debtor={debtor} {...props} />
      </div>
    </div>
  );
}

function AddDebtorModal({ onClose, onSave, settings, debtorToEdit }) {
  const [formData, setFormData] = useState({
    fullName: '',
    tcKimlik: '',
    phone: '',
    fileNumber: '',
    totalDebt: '',
    costAmount: '0',
    status: 'YENI',
    paymentPromiseDate: ''
  });

  useEffect(() => {
    if (debtorToEdit) {
      setFormData({
        fullName: debtorToEdit.fullName || '',
        tcKimlik: debtorToEdit.tcKimlik || '',
        phone: debtorToEdit.phone || '',
        fileNumber: debtorToEdit.fileNumber || '',
        totalDebt: debtorToEdit.totalDebt || '',
        costAmount: debtorToEdit.costAmount || '0',
        status: debtorToEdit.status || 'YENI',
        paymentPromiseDate: debtorToEdit.paymentPromiseDate || ''
      });
    }
  }, [debtorToEdit]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    if(!formData.fullName || !formData.fileNumber || !formData.totalDebt) {
      alert('Lütfen zorunlu alanları doldurunuz (*)');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-slate-900 text-white p-4 flex justify-between items-center">
          <h3 className="font-bold">{debtorToEdit ? 'Dosya Düzenle' : 'Yeni Dosya Açılışı'}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Dosya No *</label>
            <input name="fileNumber" value={formData.fileNumber} onChange={handleChange} className="w-full border p-2 rounded focus:ring-amber-500" placeholder="2024/..." data-testid="file-number-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Borçlu Adı Soyadı *</label>
            <input name="fullName" value={formData.fullName} onChange={handleChange} className="w-full border p-2 rounded focus:ring-amber-500" data-testid="fullname-debtor-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">TC Kimlik</label>
              <input name="tcKimlik" value={formData.tcKimlik} onChange={handleChange} className="w-full border p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Telefon</label>
              <input name="phone" value={formData.phone} onChange={handleChange} className="w-full border p-2 rounded" />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Toplam Borç (TL) *</label>
              <input name="totalDebt" type="number" value={formData.totalDebt} onChange={handleChange} className="w-full border p-2 rounded focus:ring-amber-500" placeholder="0.00" data-testid="total-debt-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">İlk Masraf (TL)</label>
              <input name="costAmount" type="number" value={formData.costAmount} onChange={handleChange} className="w-full border p-2 rounded focus:ring-amber-500" placeholder="0.00" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Başlangıç Statüsü</label>
            <select 
              name="status" 
              value={formData.status} 
              onChange={handleChange}
              className="w-full border p-2 rounded focus:ring-amber-500"
            >
              {settings.filter(s => s.category === 'STATUS_COLOR').map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>

          {formData.status === 'ODEME' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ödeme Sözü Tarihi</label>
              <input 
                type="date" 
                name="paymentPromiseDate"
                value={formData.paymentPromiseDate} 
                onChange={handleChange}
                className="w-full border p-2 rounded focus:ring-amber-500 border-amber-300 bg-amber-50"
              />
            </div>
          )}

          <button onClick={handleSubmit} className="w-full bg-amber-500 text-white py-3 rounded font-bold hover:bg-amber-600 mt-4" data-testid="save-debtor-btn">
            {debtorToEdit ? 'Değişiklikleri Kaydet' : 'Dosyayı Oluştur'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationsView({ debtors, onUpdateDate, onDeleteNotification, addDeletedLog }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newNotifDebtorId, setNewNotifDebtorId] = useState(null);
  const [newNotifDate, setNewNotifDate] = useState('');
  const [newNotifType, setNewNotifType] = useState('notificationDate');

  const activeDebtors = debtors.filter(d => !d.isClosed);

  const filteredList = activeDebtors.filter(d => {
    const { isFinalized } = calculateFinalized(d.notificationDate, d.mukhtarDate);
    const hasDate = d.notificationDate || d.mukhtarDate;
    
    const matchesSearch = d.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          d.fileNumber?.includes(searchTerm);
    
    let matchesStatus = true;
    if (filterStatus === 'FINALIZED') matchesStatus = hasDate && isFinalized;
    if (filterStatus === 'PENDING') matchesStatus = hasDate && !isFinalized;
    if (filterStatus === 'NO_DATE') matchesStatus = !hasDate;

    return matchesSearch && matchesStatus;
  });

  const handleSaveNotification = () => {
    if(!newNotifDebtorId || !newNotifDate) {
      alert("Lütfen borçlu ve tarih seçiniz.");
      return;
    }
    onUpdateDate(newNotifDebtorId, newNotifType, newNotifDate);
    setIsAddModalOpen(false);
    setNewNotifDebtorId(null);
    setNewNotifDate('');
  };

  const debtorOptions = activeDebtors.map(d => ({
    value: d.id,
    label: `${d.fileNumber} - ${d.fullName}`
  }));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-[calc(100vh-140px)]">
      <div className="p-4 border-b border-slate-200 bg-slate-50/50 rounded-t-xl space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="font-bold text-slate-700 flex items-center gap-2">
            <CalendarCheck className="text-amber-500" />
            Tebligat Tarihi Giriş ve Takip Ekranı
          </h2>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors"
          >
            <Plus size={16} /> Tebligat Girişi
          </button>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 text-slate-400 w-5 h-5" />
            <input 
              type="text" 
              placeholder="Borçlu Ara (İsim / Dosya No)..." 
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="ALL">Tüm Durumlar</option>
            <option value="FINALIZED">Kesinleşenler (Süresi Doldu)</option>
            <option value="PENDING">Süresi İşleyenler</option>
            <option value="NO_DATE">Tarih Girilmeyenler</option>
          </select>
        </div>
      </div>
      
      <div className="overflow-auto flex-1">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-100 sticky top-0 z-10 text-xs font-bold text-slate-500 uppercase tracking-wider">
            <tr>
              <th className="p-4 border-b">Dosya No</th>
              <th className="p-4 border-b">Borçlu</th>
              <th className="p-4 border-b w-48">Tebligat Tarihi</th>
              <th className="p-4 border-b w-48">Muhtar Teslim Tarihi</th>
              <th className="p-4 border-b text-center">Durum (İtiraz Süresi)</th>
              <th className="p-4 border-b text-center w-20">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredList.map(debtor => {
              const { isFinalized, daysLeft } = calculateFinalized(debtor.notificationDate, debtor.mukhtarDate);
              return (
                <tr key={debtor.id} className="hover:bg-blue-50/30 transition-colors">
                  <td className="p-4 font-medium text-slate-700">{debtor.fileNumber}</td>
                  <td className="p-4 font-medium text-slate-800">{debtor.fullName}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <input 
                        type="date" 
                        value={debtor.notificationDate || ''}
                        onChange={(e) => onUpdateDate(debtor.id, 'notificationDate', e.target.value)}
                        className="border border-slate-300 rounded px-2 py-1 text-sm flex-1 focus:ring-2 focus:ring-amber-500 outline-none"
                      />
                      {debtor.notificationDate && (
                        <button 
                          onClick={() => onDeleteNotification(debtor.id, 'notificationDate')}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Sil"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <input 
                        type="date" 
                        value={debtor.mukhtarDate || ''}
                        onChange={(e) => onUpdateDate(debtor.id, 'mukhtarDate', e.target.value)}
                        className="border border-slate-300 rounded px-2 py-1 text-sm flex-1 focus:ring-2 focus:ring-amber-500 outline-none"
                      />
                      {debtor.mukhtarDate && (
                        <button 
                          onClick={() => onDeleteNotification(debtor.id, 'mukhtarDate')}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Sil"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    {(debtor.notificationDate || debtor.mukhtarDate) ? (
                      isFinalized ? (
                        <div className="inline-flex items-center gap-2 text-red-600 bg-red-50 px-3 py-1.5 rounded-lg border border-red-100 shadow-sm">
                          <AlertTriangle size={16} />
                          <div className="text-left">
                            <div className="text-xs font-bold">KESİNLEŞTİ</div>
                            <div className="text-[10px] opacity-75">Süre Doldu</div>
                          </div>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100 shadow-sm">
                          <Calendar size={16} />
                          <div className="text-left">
                            <div className="text-xs font-bold">{Math.abs(daysLeft)} GÜN KALDI</div>
                            <div className="text-[10px] opacity-75">Süre İşliyor</div>
                          </div>
                        </div>
                      )
                    ) : (
                      <span className="text-xs text-slate-400 italic">Tarih Girilmedi</span>
                    )}
                  </td>
                  <td className="p-4 text-center">
                    {(debtor.notificationDate || debtor.mukhtarDate) && (
                      <button 
                        onClick={() => {
                          if (debtor.notificationDate) onDeleteNotification(debtor.id, 'notificationDate');
                          if (debtor.mukhtarDate) onDeleteNotification(debtor.id, 'mukhtarDate');
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded transition-colors"
                        title="Tüm tarihleri sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center">
              <h3 className="font-bold flex items-center gap-2"><Bell size={18} /> Tebligat Girişi</h3>
              <button onClick={() => setIsAddModalOpen(false)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Dosya / Borçlu Seçimi</label>
                <SearchableSelect 
                  options={debtorOptions}
                  value={newNotifDebtorId}
                  onChange={setNewNotifDebtorId}
                  placeholder="Borçlu seçiniz..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tebligat Tipi</label>
                <select 
                  value={newNotifType} 
                  onChange={(e) => setNewNotifType(e.target.value)}
                  className="w-full border p-2 rounded focus:ring-amber-500"
                >
                  <option value="notificationDate">Normal Tebligat</option>
                  <option value="mukhtarDate">Muhtar Tebligatı</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tebliğ Tarihi</label>
                <input 
                  type="date" 
                  value={newNotifDate} 
                  onChange={(e) => setNewNotifDate(e.target.value)} 
                  className="w-full border p-2 rounded focus:ring-amber-500" 
                />
              </div>
              <button 
                onClick={handleSaveNotification}
                className="w-full bg-slate-900 text-white py-3 rounded font-bold hover:bg-slate-800 mt-2"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RemittancesView({ debtors, onAddRemittance, onEditRemittance, onDeleteRemittance, addDeletedLog }) {
  const [selectedDebtorId, setSelectedDebtorId] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [editingRemittance, setEditingRemittance] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNote, setEditNote] = useState('');

  const totalCollected = debtors.reduce((acc, curr) => acc + (curr.totalPaid || 0), 0);
  const totalRemitted = debtors.reduce((acc, curr) => acc + (curr.remittedAmount || 0), 0);
  const balanceInSafe = totalCollected - totalRemitted;

  const handleSubmit = (e) => {
    e.preventDefault();
    if(!selectedDebtorId || !amount) return;
    
    // Bakiye kontrolü
    if (selectedInsideBalance <= 0) {
      alert('Bu dosyada gönderilebilecek bakiye bulunmuyor. Önce tahsilat yapılmalı.');
      return;
    }
    
    if (parseFloat(amount) > selectedInsideBalance) {
      alert(`Girilen tutar içerideki bakiyeden fazla olamaz. Maksimum: ${formatCurrency(selectedInsideBalance)}`);
      return;
    }
    
    onAddRemittance(selectedDebtorId, amount, note);
    setSuccessMsg('Reddiyat işlemi başarıyla kaydedildi.');
    
    setAmount('');
    setNote('');
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const selectedDebtor = debtors.find(d => d.id === selectedDebtorId);
  
  // Tüm açık dosyaları göster, içeride bakiye bilgisini ekle
  const debtorOptions = debtors.filter(d => !d.isClosed).map(d => {
    const insideBalance = (d.totalPaid || 0) - (d.remittedAmount || 0);
    return {
      value: d.id,
      label: `${d.fileNumber} - ${d.fullName} (İçerideki: ${formatCurrency(insideBalance)})`
    };
  });
  
  // Seçili dosyanın içerideki bakiyesi
  const selectedInsideBalance = selectedDebtor ? (selectedDebtor.totalPaid || 0) - (selectedDebtor.remittedAmount || 0) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="text-sm text-slate-500 font-medium mb-1">Toplam Tahsilat (Giriş)</div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(totalCollected)}</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="text-sm text-slate-500 font-medium mb-1">Toplam Alacaklıya Ödenen (Çıkış)</div>
          <div className="text-2xl font-bold text-blue-600">{formatCurrency(totalRemitted)}</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="text-sm text-slate-500 font-medium mb-1">Kasa Bakiyesi (Mevcut)</div>
          <div className="text-2xl font-bold text-slate-800">{formatCurrency(balanceInSafe)}</div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-280px)]">
        <div className="w-full lg:w-1/3 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
          <div className="bg-slate-900 text-white p-4 border-b border-slate-700 flex items-center gap-2">
            <ArrowUpRight size={20} className="text-amber-500"/>
            <h2 className="font-bold text-lg">Alacaklıya Ödeme (Reddiyat)</h2>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-6 flex-1 overflow-auto">
            {successMsg && (
              <div className="bg-green-50 text-green-700 p-3 rounded border border-green-200 flex items-center gap-2 text-sm">
                <CheckCircle size={16} /> {successMsg}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Dosya Seçimi</label>
              <SearchableSelect 
                options={debtorOptions}
                value={selectedDebtorId}
                onChange={setSelectedDebtorId}
                placeholder="Dosya seçiniz..."
              />
              {selectedDebtor && (
                <div className={`text-xs mt-1 ${selectedInsideBalance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {selectedInsideBalance > 0 ? (
                    <>Kullanılabilir Bakiye: <span className="font-bold">{formatCurrency(selectedInsideBalance)}</span></>
                  ) : (
                    <>⚠️ Bu dosyada gönderilebilecek bakiye yok. Önce tahsilat yapılmalı.</>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Gönderilecek Tutar (TL)</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400">₺</span>
                <input 
                  type="number" 
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg pl-8 pr-4 py-2.5 font-mono text-lg focus:ring-2 focus:ring-amber-500 outline-none"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Açıklama / Dekont No</label>
              <textarea 
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-3 h-24 focus:ring-2 focus:ring-amber-500 outline-none resize-none"
                placeholder="Örn: Şubat ayı tahsilatları gönderildi."
              ></textarea>
            </div>

            <button type="submit" className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
              <CheckCircle size={20} />
              Ödemeyi Kaydet
            </button>
          </form>
        </div>

        <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h2 className="font-bold text-slate-700">Son Yapılan Reddiyatlar</h2>
          </div>
          <div className="overflow-auto flex-1 p-0">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50 sticky top-0 z-10 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="p-4 w-32">Tarih</th>
                  <th className="p-4">Dosya / Borçlu</th>
                  <th className="p-4 text-right w-32">Tutar</th>
                  <th className="p-4">Açıklama</th>
                  <th className="p-4 w-32">İşlem Yapan</th>
                  <th className="p-4 text-center w-24">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {debtors.flatMap(d => (d.remittances || []).map(r => ({...r, debtorId: d.id, debtorName: d.fullName, fileNumber: d.fileNumber})))
                  .sort((a,b) => new Date(b.date) - new Date(a.date))
                  .map(remittance => (
                    <tr key={remittance.id} className="hover:bg-slate-50">
                      <td className="p-4 text-sm text-slate-600">{new Date(remittance.date).toLocaleDateString('tr-TR')}</td>
                      <td className="p-4">
                        <div className="font-medium text-slate-800">{remittance.debtorName}</div>
                        <div className="text-xs text-slate-500">{remittance.fileNumber}</div>
                      </td>
                      <td className="p-4 text-right">
                        {editingRemittance?.id === remittance.id ? (
                          <input 
                            type="number" 
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                            className="border border-slate-300 rounded px-2 py-1 text-sm w-24 text-right"
                          />
                        ) : (
                          <span className="font-bold text-blue-600 font-mono">{formatCurrency(remittance.amount)}</span>
                        )}
                      </td>
                      <td className="p-4">
                        {editingRemittance?.id === remittance.id ? (
                          <input 
                            type="text" 
                            value={editNote}
                            onChange={(e) => setEditNote(e.target.value)}
                            className="border border-slate-300 rounded px-2 py-1 text-sm w-full"
                          />
                        ) : (
                          <span className="text-sm text-slate-600">{remittance.note}</span>
                        )}
                      </td>
                      <td className="p-4 text-xs text-slate-500">{remittance.author}</td>
                      <td className="p-4 text-center">
                        {editingRemittance?.id === remittance.id ? (
                          <div className="flex gap-1 justify-center">
                            <button 
                              onClick={() => {
                                onEditRemittance(remittance.debtorId, remittance.id, editAmount, editNote);
                                setEditingRemittance(null);
                              }}
                              className="text-green-600 hover:bg-green-50 p-1 rounded text-xs"
                            >
                              Kaydet
                            </button>
                            <button 
                              onClick={() => setEditingRemittance(null)}
                              className="text-slate-600 hover:bg-slate-100 p-1 rounded text-xs"
                            >
                              İptal
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-center">
                            <button 
                              onClick={() => {
                                setEditingRemittance(remittance);
                                setEditAmount(remittance.amount);
                                setEditNote(remittance.note || '');
                              }}
                              className="text-blue-600 hover:bg-blue-50 p-1.5 rounded"
                              title="Düzenle"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button 
                              onClick={() => onDeleteRemittance(remittance.debtorId, remittance.id)}
                              className="text-red-600 hover:bg-red-50 p-1.5 rounded"
                              title="Sil"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                {debtors.every(d => !d.remittances || d.remittances.length === 0) && (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-slate-400">Henüz reddiyat işlemi yapılmamış.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function PaymentView({ debtors, onAddPayment }) {
  const [selectedDebtorId, setSelectedDebtorId] = useState(null);
  const [amount, setAmount] = useState('');
  const [type, setType] = useState('Nakit');
  const [note, setNote] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if(!selectedDebtorId || !amount) return;
    
    onAddPayment(selectedDebtorId, amount, type, note);
    setSuccessMsg('Ödeme başarıyla kaydedildi.');
    
    setAmount('');
    setNote('');
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const selectedDebtor = debtors.find(d => d.id === selectedDebtorId);
  const debtorOptions = debtors.filter(d => !d.isClosed).map(d => ({
    value: d.id,
    label: `${d.fileNumber} - ${d.fullName}`
  }));

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-slate-900 text-white p-4 border-b border-slate-700 flex items-center gap-2">
          <CreditCard size={20} className="text-amber-500"/>
          <h2 className="font-bold text-lg">Hızlı Tahsilat Girişi</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {successMsg && (
            <div className="bg-green-50 text-green-700 p-3 rounded border border-green-200 flex items-center gap-2 text-sm">
              <CheckCircle size={18} /> {successMsg}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Dosya / Borçlu Seçimi</label>
              <SearchableSelect 
                options={debtorOptions}
                value={selectedDebtorId}
                onChange={setSelectedDebtorId}
                placeholder="Borçlu seçiniz..."
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Ödeme Tipi</label>
              <select 
                value={type} 
                onChange={(e) => setType(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-amber-500 outline-none"
              >
                <option value="Nakit">Nakit</option>
                <option value="Kredi Kartı">Kredi Kartı</option>
                <option value="Havale">Havale / EFT</option>
                <option value="Maaş Haczi">Maaş Haczi</option>
              </select>
            </div>
          </div>

          {selectedDebtor && (
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex justify-between items-center text-sm">
              <span>Güncel Kalan Borç:</span>
              <span className="font-bold text-lg text-slate-800">{formatCurrency(selectedDebtor.balance)}</span>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Tahsilat Tutarı (TL)</label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-slate-400">₺</span>
              <input 
                type="number" 
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full border border-slate-300 rounded-lg pl-8 pr-4 py-2.5 font-mono text-lg focus:ring-2 focus:ring-amber-500 outline-none"
                placeholder="0.00"
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Ödeme Notu / Açıklama</label>
            <textarea 
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full border border-slate-300 rounded-lg p-3 h-24 focus:ring-2 focus:ring-amber-500 outline-none resize-none"
              placeholder="Örn: Şubat taksidi elden alındı."
            ></textarea>
          </div>

          <div className="pt-4">
            <button type="submit" className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
              <CheckCircle size={20} />
              Tahsilatı Onayla
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InteractionsView({ debtors, onAddInteraction }) {
  const [selectedDebtorId, setSelectedDebtorId] = useState(null);
  const [note, setNote] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const allInteractions = useMemo(() => {
    return debtors.flatMap(d => (d.notes || [])
      .filter(n => n.type === 'INTERACTION' || n.type === 'USER_NOTE')
      .map(n => ({
        ...n,
        debtorName: d.fullName,
        fileNumber: d.fileNumber,
        phone: d.phone
      }))
    ).sort((a,b) => new Date(b.date) - new Date(a.date));
  }, [debtors]);

  const filteredInteractions = allInteractions.filter(interaction => 
    interaction.debtorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    interaction.fileNumber?.includes(searchTerm)
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if(!selectedDebtorId || !note) return;

    onAddInteraction(selectedDebtorId, note);
    setNote('');
    setSelectedDebtorId(null);
    setIsAddModalOpen(false);
  };

  const debtorOptions = debtors.map(d => ({
    value: d.id,
    label: `${d.fileNumber} - ${d.fullName} (${d.phone || 'Tel Yok'})`
  }));

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] gap-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col md:flex-row justify-between items-center gap-4">
        <h2 className="font-bold text-slate-800 flex items-center gap-2 text-lg">
          <Phone className="text-amber-500" /> Görüşme Kayıtları Listesi
        </h2>
        
        <div className="flex gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-72">
            <Search className="absolute left-3 top-2.5 text-slate-400 w-5 h-5" />
            <input 
              type="text" 
              placeholder="Dosya No veya İsim Ara..." 
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium whitespace-nowrap"
          >
            <Plus size={18} />
            <span className="hidden md:inline">Yeni Görüşme Ekle</span>
            <span className="md:hidden">Ekle</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex-1 overflow-auto p-0">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-50 sticky top-0 z-10 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
            <tr>
              <th className="p-4 w-40">Tarih</th>
              <th className="p-4 w-48">Dosya / Borçlu</th>
              <th className="p-4">Görüşme Notu</th>
              <th className="p-4 w-40">Personel</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredInteractions.length === 0 ? (
              <tr>
                <td colSpan="4" className="p-8 text-center text-slate-400">
                  {searchTerm ? 'Aranan kriterlere uygun kayıt bulunamadı.' : 'Henüz kayıtlı görüşme bulunmamaktadır.'}
                </td>
              </tr>
            ) : (
              filteredInteractions.map(interaction => (
                <tr key={interaction.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-slate-400"/>
                      {new Date(interaction.date).toLocaleString('tr-TR')}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-slate-800">{interaction.debtorName}</div>
                    <div className="text-xs text-slate-500">{interaction.fileNumber}</div>
                  </td>
                  <td className="p-4 text-sm text-slate-700">{interaction.content}</td>
                  <td className="p-4 text-xs font-bold text-slate-600 bg-slate-50 rounded w-fit">
                    {interaction.author || 'Sistem'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-slate-900 text-white p-4 border-b border-slate-700 flex justify-between items-center">
              <h2 className="font-bold text-lg flex items-center gap-2">
                <Phone size={20} className="text-amber-500"/> Yeni Görüşme Ekle
              </h2>
              <button onClick={() => setIsAddModalOpen(false)} className="hover:text-amber-500"><X size={20}/></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Dosya / Borçlu Seçimi</label>
                <SearchableSelect 
                  options={debtorOptions}
                  value={selectedDebtorId}
                  onChange={setSelectedDebtorId}
                  placeholder="Görüşme yapılan kişi..."
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Görüşme Notları</label>
                <textarea 
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg p-3 h-32 focus:ring-2 focus:ring-amber-500 outline-none"
                  placeholder="Örn: Borçlu arandı, 15'inde ödeme yapacağını taahhüt etti..."
                  required
                ></textarea>
              </div>

              <div className="pt-2 flex gap-3">
                <button 
                  type="button" 
                  onClick={() => setIsAddModalOpen(false)}
                  className="flex-1 bg-slate-100 text-slate-700 py-3 rounded-lg font-bold hover:bg-slate-200 transition-colors"
                >
                  İptal
                </button>
                <button 
                  type="submit" 
                  className="flex-1 bg-slate-900 text-white py-3 rounded-lg font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
                >
                  <Save size={18} />
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsView({ settings, setSettings, users, setUsers }) {
  const { signup } = useAuth();
  const { config, updateConfig, uploadFile } = useSiteConfig();
  const [activeTab, setActiveTab] = useState('site');
  const [newLabel, setNewLabel] = useState('');
  const [newValue, setNewValue] = useState('#000000');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [uploading, setUploading] = useState({});
  const [saveSuccess, setSaveSuccess] = useState('');
  
  // Site config state
  const [siteTitle, setSiteTitle] = useState(config.siteTitle || '');
  const [siteSubtitle, setSiteSubtitle] = useState(config.siteSubtitle || '');
  const [primaryColor, setPrimaryColor] = useState(config.primaryColor || '#f59e0b');

  useEffect(() => {
    setSiteTitle(config.siteTitle || '');
    setSiteSubtitle(config.siteSubtitle || '');
    setPrimaryColor(config.primaryColor || '#f59e0b');
  }, [config]);
  
  // User form state
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserFullName, setNewUserFullName] = useState('');
  const [newUserRole, setNewUserRole] = useState('STAFF');
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [userFormError, setUserFormError] = useState('');
  const [userFormSuccess, setUserFormSuccess] = useState('');
  
  // User edit state
  const [editingUser, setEditingUser] = useState(null);
  const [editUserFullName, setEditUserFullName] = useState('');
  const [editUserRole, setEditUserRole] = useState('');

  const handleSaveSiteConfig = async () => {
    await updateConfig({
      siteTitle,
      siteSubtitle,
      primaryColor
    });
    setSaveSuccess('Ayarlar kaydedildi!');
    setTimeout(() => setSaveSuccess(''), 3000);
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Dosya boyutu kontrolü (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Dosya boyutu 5MB\'dan küçük olmalıdır.');
      return;
    }
    
    setUploading(prev => ({ ...prev, [type]: true }));
    
    try {
      // Kendi backend'imize yükle
      const url = await uploadFile(file, type);
      await updateConfig({ [type]: url });
      setSaveSuccess(`${type === 'loginImage' ? 'Giriş görseli' : type === 'logoLight' ? 'Açık logo' : type === 'logoDark' ? 'Koyu logo' : 'Favicon'} başarıyla yüklendi!`);
      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (err) {
      console.error('Upload error:', err);
      alert('Dosya yüklenirken hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    }
    
    setUploading(prev => ({ ...prev, [type]: false }));
    // Input'u temizle ki aynı dosya tekrar seçilebilsin
    e.target.value = '';
  };

  const handleRemoveImage = async (type) => {
    if (window.confirm('Bu görseli kaldırmak istediğinize emin misiniz?')) {
      await updateConfig({ [type]: '' });
    }
  };

  const handleAddStatus = async () => {
    if(!newLabel) return;
    const newSetting = {
      category: 'STATUS_COLOR',
      key: newLabel.toUpperCase().replace(/\s/g, '_'),
      value: newValue,
      label: newLabel
    };
    await addSetting(newSetting);
    setNewLabel('');
  };

  const handleDeleteStatus = async (id) => {
    if(window.confirm('Bu statüyü silmek istediğinize emin misiniz?')) {
      await deleteSetting(id);
    }
  };

  const handleEditStatus = async (id, field, value) => {
    await updateSetting(id, { [field]: value });
  };

  const handleDeleteUser = async (id) => {
    if(window.confirm('Kullanıcıyı silmek istediğinize emin misiniz?')) {
      await deleteUser(id);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditUserFullName(user.fullName);
    setEditUserRole(user.role);
  };

  const handleSaveUserEdit = async () => {
    if (!editingUser) return;
    
    try {
      await updateUser(editingUser.id, {
        fullName: editUserFullName,
        role: editUserRole
      });
      setUserFormSuccess('Kullanıcı güncellendi!');
      setEditingUser(null);
      setTimeout(() => setUserFormSuccess(''), 3000);
    } catch (err) {
      setUserFormError('Kullanıcı güncellenirken hata oluştu');
    }
  };

  const handleCancelUserEdit = () => {
    setEditingUser(null);
    setEditUserFullName('');
    setEditUserRole('');
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setUserFormError('');
    setUserFormSuccess('');
    
    if (!newUserEmail || !newUserPassword || !newUserFullName) {
      setUserFormError('Tüm alanları doldurunuz');
      return;
    }
    
    if (newUserPassword.length < 6) {
      setUserFormError('Şifre en az 6 karakter olmalıdır');
      return;
    }
    
    setUserFormLoading(true);
    
    try {
      await signup(newUserEmail, newUserPassword, newUserFullName, newUserRole);
      setUserFormSuccess(`${newUserFullName} başarıyla eklendi!`);
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserFullName('');
      setNewUserRole('STAFF');
      setTimeout(() => setUserFormSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') {
        setUserFormError('Bu e-posta zaten kayıtlı');
      } else if (err.code === 'auth/weak-password') {
        setUserFormError('Şifre en az 6 karakter olmalıdır');
      } else if (err.code === 'auth/invalid-email') {
        setUserFormError('Geçersiz e-posta adresi');
      } else {
        setUserFormError('Kullanıcı eklenirken hata oluştu');
      }
    }
    
    setUserFormLoading(false);
  };

  return (
    <div className="grid gap-6">
      {/* Success Message */}
      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm animate-fade-in">
          <CheckCircle size={18} /> {saveSuccess}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        <button 
          onClick={() => setActiveTab('site')} 
          className={`px-4 py-2 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${activeTab === 'site' ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
        >
          <Globe size={16} className="inline mr-2" />
          Site Ayarları
        </button>
        <button 
          onClick={() => setActiveTab('statuses')} 
          className={`px-4 py-2 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${activeTab === 'statuses' ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
        >
          <Palette size={16} className="inline mr-2" />
          Durum Statüleri
        </button>
        <button 
          onClick={() => setActiveTab('users')} 
          className={`px-4 py-2 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${activeTab === 'users' ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
        >
          <Users size={16} className="inline mr-2" />
          Kullanıcılar
        </button>
      </div>

      {/* Site Settings Tab */}
      {activeTab === 'site' && (
        <div className="space-y-6">
          {/* General Settings */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-slate-800">
              <Type size={20} /> Genel Ayarlar
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Site Başlığı</label>
                <input 
                  type="text"
                  value={siteTitle}
                  onChange={(e) => setSiteTitle(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  placeholder="BERKAY ÇAKIR"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Alt Başlık</label>
                <input 
                  type="text"
                  value={siteSubtitle}
                  onChange={(e) => setSiteSubtitle(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  placeholder="Hukuk Bürosu"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">Ana Renk</label>
              <div className="flex items-center gap-4">
                <div 
                  className="w-14 h-14 rounded-xl cursor-pointer shadow-lg border-4 border-white"
                  style={{ backgroundColor: primaryColor }}
                  onClick={() => setShowColorPicker(!showColorPicker)}
                />
                <div className="flex-1">
                  <input 
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-full max-w-xs border border-slate-300 rounded-xl px-4 py-2 font-mono text-sm"
                  />
                </div>
              </div>
              {showColorPicker && (
                <div className="mt-4 p-4 bg-slate-50 rounded-xl inline-block">
                  <HexColorPicker color={primaryColor} onChange={setPrimaryColor} />
                </div>
              )}
            </div>

            <button 
              onClick={handleSaveSiteConfig}
              className="mt-6 px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors flex items-center gap-2"
            >
              <Save size={18} /> Kaydet
            </button>
          </div>

          {/* Logo & Images */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-slate-800">
              <Image size={20} /> Logo ve Görseller
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Login Image */}
              <div className="col-span-full">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Giriş Sayfası Görseli</label>
                <p className="text-xs text-slate-500 mb-3">Giriş sayfasının sol tarafında görünecek görsel</p>
                <div className="flex items-start gap-4">
                  {config.loginImage ? (
                    <div className="relative group">
                      <img src={config.loginImage} alt="Login" className="w-40 h-24 object-cover rounded-xl shadow-md" />
                      <button 
                        onClick={() => handleRemoveImage('loginImage')}
                        className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-40 h-24 bg-slate-100 rounded-xl flex items-center justify-center">
                      <Image size={24} className="text-slate-400" />
                    </div>
                  )}
                  <label className="flex-1 cursor-pointer">
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 hover:border-amber-500 hover:bg-amber-50 transition-all text-center">
                      {uploading.loginImage ? (
                        <div className="text-amber-600">Yükleniyor...</div>
                      ) : (
                        <>
                          <Upload size={20} className="mx-auto text-slate-400 mb-2" />
                          <span className="text-sm text-slate-600">Dosya Seç</span>
                        </>
                      )}
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'loginImage')} />
                  </label>
                </div>
              </div>

              {/* Light Logo */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Logo (Açık Tema)</label>
                <p className="text-xs text-slate-500 mb-3">Koyu arka plan için (PNG)</p>
                <div className="flex items-start gap-4">
                  {config.logoLight ? (
                    <div className="relative group">
                      <div className="w-24 h-16 bg-slate-800 rounded-xl p-2 flex items-center justify-center">
                        <img src={config.logoLight} alt="Logo Light" className="max-w-full max-h-full object-contain" />
                      </div>
                      <button 
                        onClick={() => handleRemoveImage('logoLight')}
                        className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-16 bg-slate-800 rounded-xl flex items-center justify-center">
                      <Gavel size={20} className="text-slate-500" />
                    </div>
                  )}
                  <label className="flex-1 cursor-pointer">
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-3 hover:border-amber-500 transition-all text-center">
                      {uploading.logoLight ? 'Yükleniyor...' : <><Upload size={16} className="mx-auto text-slate-400 mb-1" /><span className="text-xs text-slate-600">Dosya Seç</span></>}
                    </div>
                    <input type="file" accept="image/png" className="hidden" onChange={(e) => handleFileUpload(e, 'logoLight')} />
                  </label>
                </div>
              </div>

              {/* Dark Logo */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Logo (Koyu Tema)</label>
                <p className="text-xs text-slate-500 mb-3">Açık arka plan için (PNG)</p>
                <div className="flex items-start gap-4">
                  {config.logoDark ? (
                    <div className="relative group">
                      <div className="w-24 h-16 bg-white border border-slate-200 rounded-xl p-2 flex items-center justify-center">
                        <img src={config.logoDark} alt="Logo Dark" className="max-w-full max-h-full object-contain" />
                      </div>
                      <button 
                        onClick={() => handleRemoveImage('logoDark')}
                        className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-16 bg-white border border-slate-200 rounded-xl flex items-center justify-center">
                      <Gavel size={20} className="text-slate-400" />
                    </div>
                  )}
                  <label className="flex-1 cursor-pointer">
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-3 hover:border-amber-500 transition-all text-center">
                      {uploading.logoDark ? 'Yükleniyor...' : <><Upload size={16} className="mx-auto text-slate-400 mb-1" /><span className="text-xs text-slate-600">Dosya Seç</span></>}
                    </div>
                    <input type="file" accept="image/png" className="hidden" onChange={(e) => handleFileUpload(e, 'logoDark')} />
                  </label>
                </div>
              </div>

              {/* Favicon */}
              <div className="col-span-full">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Favicon</label>
                <p className="text-xs text-slate-500 mb-3">Tarayıcı sekmesinde görünecek ikon (ICO, PNG)</p>
                <div className="flex items-start gap-4">
                  {config.favicon ? (
                    <div className="relative group">
                      <img src={config.favicon} alt="Favicon" className="w-12 h-12 object-contain rounded-lg shadow-md" />
                      <button 
                        onClick={() => handleRemoveImage('favicon')}
                        className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Globe size={20} className="text-slate-400" />
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed border-slate-300 rounded-xl px-4 py-2 hover:border-amber-500 transition-all text-center">
                      {uploading.favicon ? 'Yükleniyor...' : <><Upload size={16} className="inline mr-2 text-slate-400" /><span className="text-sm text-slate-600">Dosya Seç</span></>}
                    </div>
                    <input type="file" accept="image/x-icon,image/png" className="hidden" onChange={(e) => handleFileUpload(e, 'favicon')} />
                  </label>
                </div>
              </div>
            </div>

            {/* Save Button for Logo & Images */}
            <div className="mt-6 pt-6 border-t border-slate-100">
              <p className="text-sm text-slate-500 mb-4">
                Yüklenen görseller otomatik olarak kaydedilir. Sorun yaşıyorsanız sayfayı yenileyip tekrar deneyin.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'statuses' && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><Settings className="w-5 h-5" /> Statü Düzenleme</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {settings.filter(s => s.category === 'STATUS_COLOR').map(s => (
              <div key={s.id} className="flex items-center gap-3 p-3 border rounded-xl hover:bg-slate-50 group">
                <input 
                  type="color" 
                  value={s.value} 
                  onChange={(e) => handleEditStatus(s.id, 'value', e.target.value)}
                  className="w-8 h-8 rounded-lg shadow-sm border-0 p-0 cursor-pointer"
                />
                <div className="flex-1">
                  <input 
                    type="text" 
                    value={s.label}
                    onChange={(e) => handleEditStatus(s.id, 'label', e.target.value)}
                    className="font-bold text-sm text-slate-800 bg-transparent border-none p-0 focus:ring-0 w-full"
                  />
                  <div className="text-xs text-slate-400 font-mono">{s.key}</div>
                </div>
                <button onClick={() => handleDeleteStatus(s.id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
          
          <div className="mt-6 pt-6 border-t border-slate-100">
            <h4 className="text-sm font-semibold mb-3 text-slate-600">Yeni Statü Ekle</h4>
            <div className="flex gap-2">
              <input 
                type="text" 
                placeholder="Statü Adı (Örn: İstihbarat)" 
                className="flex-1 border p-2 rounded focus:ring-amber-500"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
              />
              <input 
                type="color" 
                className="h-10 w-12 p-1 border rounded cursor-pointer"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
              />
              <button 
                onClick={handleAddStatus}
                className="bg-amber-500 text-white px-4 py-2 rounded hover:bg-amber-600"
              >
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* User List */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> Mevcut Kullanıcılar</h3>
            
            <div className="overflow-hidden border rounded-lg">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ad Soyad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">E-posta</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Yetki</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {users.map(user => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUser?.id === user.id ? (
                          <input 
                            type="text" 
                            value={editUserFullName}
                            onChange={(e) => setEditUserFullName(e.target.value)}
                            className="border border-slate-300 rounded px-2 py-1 text-sm w-full"
                          />
                        ) : (
                          <span className="text-sm font-medium text-slate-900">{user.fullName}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUser?.id === user.id ? (
                          <select 
                            value={editUserRole}
                            onChange={(e) => setEditUserRole(e.target.value)}
                            className="border border-slate-300 rounded px-2 py-1 text-sm"
                          >
                            <option value="STAFF">Personel</option>
                            <option value="LAWYER">Avukat</option>
                            <option value="ADMIN">Yönetici</option>
                          </select>
                        ) : (
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.role === 'ADMIN' ? 'bg-purple-100 text-purple-800' : 
                            user.role === 'LAWYER' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {user.role === 'ADMIN' ? 'Yönetici' : user.role === 'LAWYER' ? 'Avukat' : 'Personel'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {editingUser?.id === user.id ? (
                          <div className="flex justify-end gap-2">
                            <button 
                              onClick={handleSaveUserEdit}
                              className="text-green-600 hover:text-green-900 px-2 py-1 bg-green-50 rounded"
                            >
                              Kaydet
                            </button>
                            <button 
                              onClick={handleCancelUserEdit}
                              className="text-slate-600 hover:text-slate-900 px-2 py-1 bg-slate-50 rounded"
                            >
                              İptal
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <button 
                              onClick={() => handleEditUser(user)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Düzenle
                            </button>
                            <button 
                              onClick={() => handleDeleteUser(user.id)} 
                              className="text-red-600 hover:text-red-900"
                            >
                              Sil
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-slate-400">Henüz kullanıcı yok</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Add User Form */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><UserPlus className="w-5 h-5" /> Yeni Kullanıcı Ekle</h3>
            
            {userFormError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                {userFormError}
              </div>
            )}
            
            {userFormSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-center gap-2">
                <CheckCircle size={16} /> {userFormSuccess}
              </div>
            )}
            
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ad Soyad *</label>
                  <input 
                    type="text" 
                    value={newUserFullName}
                    onChange={(e) => setNewUserFullName(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-amber-500 outline-none"
                    placeholder="Örn: Av. Mehmet Yılmaz"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">E-posta *</label>
                  <input 
                    type="email" 
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-amber-500 outline-none"
                    placeholder="ornek@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Şifre * (min 6 karakter)</label>
                  <input 
                    type="password" 
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-amber-500 outline-none"
                    placeholder="••••••••"
                    minLength={6}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Yetki Seviyesi</label>
                  <select 
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-amber-500 outline-none"
                  >
                    <option value="STAFF">Personel</option>
                    <option value="LAWYER">Avukat</option>
                    <option value="ADMIN">Yönetici</option>
                  </select>
                </div>
              </div>
              
              <button 
                type="submit"
                disabled={userFormLoading}
                className="bg-slate-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {userFormLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Ekleniyor...
                  </>
                ) : (
                  <>
                    <UserPlus size={18} />
                    Kullanıcı Ekle
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportsView({ debtors, settings }) {
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [compareMode, setCompareMode] = useState(true);
  
  const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  // Yılları oluştur (son 5 yıl)
  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);

  // Seçili ay verileri
  const getMonthData = (year, month) => {
    const payments = debtors.flatMap(d => (d.payments || []).filter(p => {
      const date = new Date(p.date);
      return date.getFullYear() === year && date.getMonth() + 1 === month;
    }));

    const interactions = debtors.flatMap(d => (d.notes || []).filter(n => {
      const date = new Date(n.date);
      return date.getFullYear() === year && date.getMonth() + 1 === month && 
             (n.type === 'INTERACTION' || n.type === 'USER_NOTE');
    }));

    const newFiles = debtors.filter(d => {
      if (!d.createdAt) return false;
      const date = new Date(d.createdAt.seconds ? d.createdAt.seconds * 1000 : d.createdAt);
      return date.getFullYear() === year && date.getMonth() + 1 === month;
    });

    const closedFiles = debtors.filter(d => {
      const closeNote = (d.notes || []).find(n => n.content?.includes('Dosya kapatıldı'));
      if (!closeNote) return false;
      const date = new Date(closeNote.date);
      return date.getFullYear() === year && date.getMonth() + 1 === month;
    });

    return {
      totalPayments: payments.reduce((sum, p) => sum + (p.amount || 0), 0),
      paymentCount: payments.length,
      interactionCount: interactions.length,
      newFilesCount: newFiles.length,
      closedFilesCount: closedFiles.length
    };
  };

  const currentMonthData = getMonthData(selectedYear, selectedMonth);
  
  // Önceki ay (karşılaştırma için)
  const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
  const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
  const prevMonthData = getMonthData(prevYear, prevMonth);

  // Yıllık toplam
  const yearlyData = useMemo(() => {
    let totalPayments = 0;
    let totalInteractions = 0;
    let totalNewFiles = 0;
    
    for (let m = 1; m <= 12; m++) {
      const data = getMonthData(selectedYear, m);
      totalPayments += data.totalPayments;
      totalInteractions += data.interactionCount;
      totalNewFiles += data.newFilesCount;
    }
    
    return { totalPayments, totalInteractions, totalNewFiles };
  }, [selectedYear, debtors]);

  // Aylık grafik verisi
  const monthlyChartData = useMemo(() => {
    const data = [];
    for (let m = 1; m <= 12; m++) {
      const monthData = getMonthData(selectedYear, m);
      data.push({
        month: monthNames[m - 1].slice(0, 3),
        fullMonth: monthNames[m - 1],
        amount: monthData.totalPayments,
        count: monthData.paymentCount
      });
    }
    return data;
  }, [selectedYear, debtors]);

  const maxMonthlyPayment = Math.max(...monthlyChartData.map(d => d.amount), 1);

  // Genel istatistikler
  const totalDebt = debtors.reduce((acc, curr) => acc + (curr.totalDebt || 0), 0);
  const totalPaid = debtors.reduce((acc, curr) => acc + (curr.totalPaid || 0), 0);
  const collectionRate = totalDebt > 0 ? (totalPaid / totalDebt) * 100 : 0;

  // Değişim hesaplama
  const calculateChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const paymentChange = calculateChange(currentMonthData.totalPayments, prevMonthData.totalPayments);
  const interactionChange = calculateChange(currentMonthData.interactionCount, prevMonthData.interactionCount);

  return (
    <div className="space-y-6">
      {/* Filtreler */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-600">Yıl:</label>
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-600">Ay:</label>
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              {monthNames.map((name, idx) => <option key={idx} value={idx + 1}>{name}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer ml-auto">
            <input 
              type="checkbox" 
              checked={compareMode} 
              onChange={(e) => setCompareMode(e.target.checked)}
              className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
            />
            <span className="text-sm text-slate-600">Önceki Ay ile Karşılaştır</span>
          </label>
        </div>
      </div>

      {/* Seçili Ay Özeti */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-2xl text-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-bold text-xl">
            {monthNames[selectedMonth - 1]} {selectedYear} Raporu
          </h3>
          {compareMode && (
            <span className="text-sm text-slate-400">
              vs {monthNames[prevMonth - 1]} {prevYear}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 p-4 rounded-xl">
            <div className="text-slate-300 text-sm mb-1">Toplam Tahsilat</div>
            <div className="text-2xl font-bold">{formatCurrency(currentMonthData.totalPayments)}</div>
            {compareMode && (
              <div className={`text-sm mt-2 flex items-center gap-1 ${paymentChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {paymentChange >= 0 ? <ArrowUpRight size={14} /> : <ArrowUpRight size={14} className="rotate-90" />}
                %{Math.abs(paymentChange).toFixed(1)}
              </div>
            )}
          </div>

          <div className="bg-white/10 p-4 rounded-xl">
            <div className="text-slate-300 text-sm mb-1">Ödeme Sayısı</div>
            <div className="text-2xl font-bold">{currentMonthData.paymentCount}</div>
            {compareMode && <div className="text-sm mt-2 text-slate-400">Önceki: {prevMonthData.paymentCount}</div>}
          </div>

          <div className="bg-white/10 p-4 rounded-xl">
            <div className="text-slate-300 text-sm mb-1">Görüşme Sayısı</div>
            <div className="text-2xl font-bold">{currentMonthData.interactionCount}</div>
            {compareMode && (
              <div className={`text-sm mt-2 flex items-center gap-1 ${interactionChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {interactionChange >= 0 ? <ArrowUpRight size={14} /> : <ArrowUpRight size={14} className="rotate-90" />}
                %{Math.abs(interactionChange).toFixed(1)}
              </div>
            )}
          </div>

          <div className="bg-white/10 p-4 rounded-xl">
            <div className="text-slate-300 text-sm mb-1">Yeni / Kapatılan</div>
            <div className="text-2xl font-bold">
              <span className="text-emerald-400">+{currentMonthData.newFilesCount}</span>
              {' / '}
              <span className="text-red-400">-{currentMonthData.closedFilesCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Aylık Tahsilat Grafiği */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h3 className="font-bold text-lg mb-6 text-slate-800 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-500" /> {selectedYear} Yılı Aylık Tahsilat Grafiği
        </h3>
        
        <div className="flex items-end gap-2 h-64">
          {monthlyChartData.map((data, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center">
              <div className="text-xs font-bold text-slate-600 mb-1">
                {data.amount > 0 ? formatCurrency(data.amount).replace('₺', '').trim() : ''}
              </div>
              <div 
                className={`w-full rounded-t transition-all duration-500 ${
                  idx + 1 === selectedMonth ? 'bg-amber-500' : 'bg-blue-500'
                }`}
                style={{ 
                  height: `${(data.amount / maxMonthlyPayment) * 200}px`,
                  minHeight: data.amount > 0 ? '4px' : '2px',
                  opacity: data.amount > 0 ? 1 : 0.3
                }}
                title={`${data.fullMonth}: ${formatCurrency(data.amount)}`}
              />
              <div className="text-xs text-slate-500 mt-2 font-medium">{data.month}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100 flex justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span className="text-slate-600">Diğer Aylar</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-500 rounded"></div>
            <span className="text-slate-600">Seçili Ay</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Genel İstatistikler */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h3 className="font-bold text-lg mb-6 text-slate-800 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-amber-500" /> Genel İstatistikler
          </h3>
          
          <div className="space-y-6">
            <div className="flex flex-col items-center justify-center p-4 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-500 mb-1">Genel Tahsilat Oranı</div>
              <div className="text-4xl font-bold text-emerald-600">%{collectionRate.toFixed(1)}</div>
              <div className="w-full bg-slate-200 h-3 mt-3 rounded-full overflow-hidden">
                <div className="bg-emerald-500 h-full transition-all duration-500" style={{ width: `${collectionRate}%` }}></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <div className="text-xs text-blue-600 font-medium mb-1">Toplam Alacak</div>
                <div className="text-lg font-bold text-blue-700">{formatCurrency(totalDebt)}</div>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg text-center">
                <div className="text-xs text-emerald-600 font-medium mb-1">Toplam Tahsilat</div>
                <div className="text-lg font-bold text-emerald-700">{formatCurrency(totalPaid)}</div>
              </div>
            </div>

            <div className="p-4 bg-red-50 rounded-lg text-center">
              <div className="text-xs text-red-600 font-medium mb-1">Bekleyen Bakiye (Risk)</div>
              <div className="text-2xl font-bold text-red-700">{formatCurrency(totalDebt - totalPaid)}</div>
            </div>
          </div>
        </div>

        {/* Yıllık Özet ve Durum Dağılımı */}
        <div className="space-y-6">
          {/* Yıllık Özet */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h3 className="font-bold text-lg mb-4 text-slate-800">{selectedYear} Yıllık Özet</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <div className="text-xs text-emerald-600 mb-1">Yıllık Tahsilat</div>
                <div className="font-bold text-emerald-700">{formatCurrency(yearlyData.totalPayments)}</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-xs text-blue-600 mb-1">Toplam Görüşme</div>
                <div className="font-bold text-blue-700">{yearlyData.totalInteractions}</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-xs text-purple-600 mb-1">Yeni Dosya</div>
                <div className="font-bold text-purple-700">{yearlyData.totalNewFiles}</div>
              </div>
            </div>
          </div>

          {/* Dosya Durum Dağılımı */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h3 className="font-bold text-sm uppercase tracking-wide text-slate-500 mb-4">Dosya Durum Dağılımı</h3>
            <div className="space-y-3">
              {settings.filter(s => s.category === 'STATUS_COLOR').map(setting => {
                const count = debtors.filter(d => d.status === setting.key).length;
                const percentage = (count / debtors.length) * 100 || 0;
                return (
                  <div key={setting.key} className="flex items-center gap-3">
                    <div className="w-24 text-xs font-medium text-slate-600 truncate">{setting.label}</div>
                    <div className="flex-1 bg-slate-100 h-3 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500" 
                        style={{ width: `${percentage}%`, backgroundColor: setting.value }}
                      ></div>
                    </div>
                    <div className="w-12 text-xs font-bold text-right">{count} (%{percentage.toFixed(0)})</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- DELETED LOGS VIEW ---

function DeletedLogsView({ deletedLogs }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');

  const filteredLogs = deletedLogs.filter(log => {
    const matchesSearch = log.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          log.deletedBy?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'ALL' || log.type === filterType;
    return matchesSearch && matchesType;
  });

  const logTypes = [...new Set(deletedLogs.map(l => l.type))];

  const getTypeIcon = (type) => {
    switch(type) {
      case 'Tebligat': return <Bell size={16} className="text-amber-500" />;
      case 'Reddiyat': return <Wallet size={16} className="text-blue-500" />;
      case 'Tahsilat': return <CreditCard size={16} className="text-green-500" />;
      case 'Dosya': return <FileText size={16} className="text-purple-500" />;
      default: return <Trash2 size={16} className="text-red-500" />;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch(type) {
      case 'Tebligat': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Reddiyat': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Tahsilat': return 'bg-green-100 text-green-700 border-green-200';
      case 'Dosya': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-red-100 text-red-700 border-red-200';
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col h-[calc(100vh-140px)]">
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50 rounded-t-xl space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="font-bold text-slate-700 dark:text-slate-200 flex items-center gap-2">
            <Trash2 className="text-red-500" />
            Silinen İşlemler Logu
          </h2>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Toplam: {deletedLogs.length} kayıt
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 text-slate-400 w-5 h-5" />
            <input 
              type="text" 
              placeholder="Açıklama veya kullanıcı ara..." 
              className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white dark:bg-slate-700 dark:text-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            className="border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white dark:bg-slate-700 dark:text-white"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="ALL">Tüm İşlem Türleri</option>
            {logTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="overflow-auto flex-1">
        {filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 dark:text-slate-500">
            <Trash2 size={48} className="mb-4 opacity-50" />
            <p className="text-lg font-medium">Silinen işlem bulunamadı</p>
            <p className="text-sm">Silinen işlemler burada listelenecektir.</p>
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-100 dark:bg-slate-700 sticky top-0 z-10 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="p-4 border-b dark:border-slate-600">Tarih/Saat</th>
                <th className="p-4 border-b dark:border-slate-600">Tür</th>
                <th className="p-4 border-b dark:border-slate-600">Açıklama</th>
                <th className="p-4 border-b dark:border-slate-600">Detaylar</th>
                <th className="p-4 border-b dark:border-slate-600">Silen Kullanıcı</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {filteredLogs.map(log => (
                <tr key={log.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="p-4 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-slate-400" />
                      {new Date(log.deletedAt).toLocaleString('tr-TR')}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${getTypeBadgeColor(log.type)}`}>
                      {getTypeIcon(log.type)}
                      {log.type}
                    </span>
                  </td>
                  <td className="p-4 text-slate-700 dark:text-slate-200 font-medium">
                    {log.description}
                  </td>
                  <td className="p-4 text-sm text-slate-500 dark:text-slate-400">
                    {log.details && (
                      <div className="space-y-0.5">
                        {log.details.debtorName && (
                          <div className="flex items-center gap-1">
                            <User size={12} />
                            <span>{log.details.debtorName}</span>
                          </div>
                        )}
                        {log.details.fileNumber && (
                          <div className="flex items-center gap-1">
                            <FileText size={12} />
                            <span>Dosya: {log.details.fileNumber}</span>
                          </div>
                        )}
                        {log.details.amount && (
                          <div className="flex items-center gap-1">
                            <DollarSign size={12} />
                            <span>{formatCurrency(log.details.amount)}</span>
                          </div>
                        )}
                        {log.details.date && (
                          <div className="flex items-center gap-1">
                            <Calendar size={12} />
                            <span>{log.details.date}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 font-bold text-xs">
                        {log.deletedBy?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                      </div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{log.deletedBy}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// --- ROOT APP WITH AUTH ---

function App() {
  const { currentUser, loading } = useAuth();

  // Setup sayfası güvenlik nedeniyle kaldırıldı

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return currentUser ? <MainApp /> : <Login />;
}

// Wrap with AuthProvider and SiteConfigProvider
function AppWrapper() {
  return (
    <SiteConfigProvider>
      <AppWithConfigLoading />
    </SiteConfigProvider>
  );
}

// Config yüklenene kadar loading göster - flash sorununu çözer
function AppWithConfigLoading() {
  const { loading: configLoading } = useSiteConfig();
  
  // Site config yüklenene kadar tam ekran loading göster
  if (configLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWrapper;
